import React, { useState, useEffect, useRef } from 'react';
import { useData, DOCTOR_INFO } from '../context/DataContext';
import useApiPatients from '../hooks/useApiPatients';
import useApiStaff from '../hooks/useApiStaff';
import { useLoader } from '../context/LoaderContext';
import useApiBabies from '../hooks/useApiBabies';
import useApiBills from '../hooks/useApiBills';
import { fetchPatientById, createPatientBill, updatePatientBill, fetchDischargeRequests, updateDischargeRequest, fetchStaffById, createStaff, updateStaff, fetchAllIncomeExpense, createIncomeExpense, updateIncomeExpense, getPatientDropdown } from '../services/api';

const AdminPanel = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { patients, bills, birthRecords, transactions, getStats, USERS, approveDischarge, getPatientDues, complaints, incidents, staffCredentials, addComplaint, updateComplaint, addIncident, addStaffCredential, updateStaffCredential } = useData();
  const stats = getStats();

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'patients', label: 'Patients', icon: '👥' },
    { id: 'bills', label: 'Bills', icon: '🧾' },
    { id: 'finance', label: 'Income/Expense', icon: '💰' },
    { id: 'discharge', label: 'Discharge Approval', icon: '✅' },
    { id: 'births', label: 'Birth Certificates', icon: '👶' },
    { id: 'complaints', label: 'Complaints (PHC)', icon: '📝' },
    { id: 'incidents', label: 'Incidents (PHC)', icon: '⚠️' },
    { id: 'staff', label: 'Staff Credentials', icon: '🪪' },
    { id: 'passwords', label: 'Passwords', icon: '🔐' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Logo" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-gray-900">Admin Panel</h1>
              <p className="text-xs text-gray-500">Financial & Records Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">⚙️ {user.name}</span>
            <button onClick={onLogout} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="stat-card bg-blue-50 border border-blue-200">
            <p className="text-blue-600 text-xs font-medium">Total Patients</p>
            <p className="text-2xl font-bold text-blue-700">{stats.totalPatients}</p>
          </div>
          <div className="stat-card bg-green-50 border border-green-200">
            <p className="text-green-600 text-xs font-medium">Total Income</p>
            <p className="text-2xl font-bold text-green-700">Rs. {stats.totalIncome.toLocaleString()}</p>
          </div>
          <div className="stat-card bg-red-50 border border-red-200">
            <p className="text-red-600 text-xs font-medium">Total Expenses</p>
            <p className="text-2xl font-bold text-red-700">Rs. {stats.totalExpenses.toLocaleString()}</p>
          </div>
          <div className="stat-card bg-yellow-50 border border-yellow-200">
            <p className="text-yellow-600 text-xs font-medium">Pending Bills</p>
            <p className="text-2xl font-bold text-yellow-700">Rs. {stats.pendingBills.toLocaleString()}</p>
          </div>
          <div className={`stat-card ${stats.netBalance >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
            <p className={`text-xs font-medium ${stats.netBalance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>Net Balance</p>
            <p className={`text-2xl font-bold ${stats.netBalance >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Rs. {stats.netBalance.toLocaleString()}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <span>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>
{activeTab === 'dashboard' && <DashboardView stats={stats} transactions={transactions} bills={bills} activeTab={activeTab} />}

        {activeTab === 'patients' && <PatientRecords patients={patients} bills={bills} />}
        {activeTab === 'bills' && <BillsManagement />}
        {activeTab === 'finance' && <FinanceManagement />}
        {activeTab === 'discharge' && <DischargeApproval />}
        {activeTab === 'births' && <BirthCertificates birthRecords={birthRecords} />}
        {activeTab === 'complaints' && <ComplaintsManagement />}
        {activeTab === 'incidents' && <IncidentsManagement />}
        {activeTab === 'staff' && <StaffCredentials />}
        {activeTab === 'passwords' && <PasswordReset users={USERS} />}
      </div>
    </div>
  );
};

// Dashboard View
const DashboardView = ({ stats, transactions, bills }) => {
  const { showLoader, hideLoader } = useLoader();
  const [apiTransactions, setApiTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      showLoader('Loading dashboard...');
      try {
        const data = await fetchAllIncomeExpense();
        setApiTransactions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn('Dashboard API error:', err.message);
      }
      setLoading(false);
      hideLoader();
    };
    load();
  }, []);

  // Map API transactions to display format
  const mappedTransactions = apiTransactions.map(t => ({
    id: `api-${t.id}`,
    type: t.type === 'Income' ? 'income' : 'expense',
    category: t.category && t.category !== 'string' ? t.category : 'Other',
    description: t.description && t.description !== 'string' ? t.description : '',
    amount: t.amount,
    date: t.expenseDate ? t.expenseDate.split('T')[0] : '',
  }));

  const displayTransactions = mappedTransactions.length > 0 ? mappedTransactions : transactions;

  const today = new Date().toISOString().split('T')[0];
  const todayTransactions = displayTransactions.filter(t => t.date === today);
  const todayIncome = todayTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + Number(t.amount), 0);
  const todayExpense = todayTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount), 0);

  // Overall stats from API
  const totalIncome = displayTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + Number(t.amount), 0);
  const totalExpense = displayTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount), 0);

  const unpaidBills = bills.filter(b => b.status !== 'paid');
  const recentTransactions = displayTransactions.slice().reverse().slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Today's Summary - {new Date().toLocaleDateString()}</h2>
        {loading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading live data...</span>}
        {!loading && mappedTransactions.length > 0 && (
          <span className="text-xs text-green-600">✅ Live API ({mappedTransactions.length} records)</span>
        )}
      </div>

      {/* Today's Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <p className="text-green-600 font-medium mb-2">Today's Income</p>
          <p className="text-3xl font-bold text-green-700">Rs. {todayIncome.toLocaleString()}</p>
        </div>
        <div className="card p-6 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <p className="text-red-600 font-medium mb-2">Today's Expenses</p>
          <p className="text-3xl font-bold text-red-700">Rs. {todayExpense.toLocaleString()}</p>
        </div>
        <div className={`card p-6 ${todayIncome - todayExpense >= 0 ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200' : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'}`}>
          <p className={`font-medium mb-2 ${todayIncome - todayExpense >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Today's Net</p>
          <p className={`text-3xl font-bold ${todayIncome - todayExpense >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>Rs. {(todayIncome - todayExpense).toLocaleString()}</p>
        </div>
      </div>

     {/* Overall Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card p-4 bg-green-50 border border-green-200">
          <p className="text-green-600 text-xs font-medium uppercase tracking-wide">All-Time Income</p>
          <p className="text-2xl font-bold text-green-700">Rs. {totalIncome.toLocaleString()}</p>
          <p className="text-xs text-green-500 mt-1">Today: Rs. {todayIncome.toLocaleString()}</p>
        </div>
        <div className="card p-4 bg-red-50 border border-red-200">
          <p className="text-red-600 text-xs font-medium uppercase tracking-wide">All-Time Expenses</p>
          <p className="text-2xl font-bold text-red-700">Rs. {totalExpense.toLocaleString()}</p>
          <p className="text-xs text-red-500 mt-1">Today: Rs. {todayExpense.toLocaleString()}</p>
        </div>
        <div className={`card p-4 ${totalIncome - totalExpense >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
          <p className={`text-xs font-medium uppercase tracking-wide ${totalIncome - totalExpense >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>Net Balance</p>
          <p className={`text-2xl font-bold ${totalIncome - totalExpense >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Rs. {(totalIncome - totalExpense).toLocaleString()}</p>
          <p className={`text-xs mt-1 ${todayIncome - todayExpense >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>Today: Rs. {(todayIncome - todayExpense).toLocaleString()}</p>
        </div>
      </div>

      {/* Pending Bills */}
      <div className="card p-6">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-yellow-500">⚠️</span> Pending Bills ({unpaidBills.length})
        </h3>
        {unpaidBills.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>MR No</th><th>Patient</th><th>Total</th><th>Paid</th><th>Due</th><th>Status</th></tr>
              </thead>
              <tbody>
                {unpaidBills.slice(0, 5).map(bill => (
                  <tr key={bill.id}>
                    <td className="font-mono text-sm">{bill.mrNo}</td>
                    <td>{bill.patientName}</td>
                    <td>Rs. {Number(bill.totalAmount).toLocaleString()}</td>
                    <td className="text-green-600">Rs. {Number(bill.paidAmount || 0).toLocaleString()}</td>
                    <td className="text-red-600 font-bold">Rs. {(Number(bill.totalAmount) - Number(bill.paidAmount || 0)).toLocaleString()}</td>
                    <td><span className={`status-badge status-${bill.status}`}>{bill.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-green-600">✓ All bills are paid!</p>}
      </div>

      {/* Recent Transactions - max 3 */}
      <div className="card p-6">
        <h3 className="font-bold text-gray-900 mb-4">
          Recent Transactions
          <span className="text-xs font-normal text-gray-400 ml-2">(last 3)</span>
        </h3>
        {recentTransactions.length > 0 ? (
          <div className="space-y-2">
            {recentTransactions.map(t => (
              <div key={t.id} className="flex justify-between items-center py-2 border-b last:border-0">
                <div>
                  <p className="font-medium text-gray-900">{t.description || t.category}</p>
                  <p className="text-xs text-gray-500">{t.category} • {t.date}</p>
                </div>
                <span className={`font-bold ${t.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                  {t.type === 'income' ? '+' : '-'} Rs. {Number(t.amount).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No transactions yet</p>
        )}
      </div>
    </div>
  );
};

const PatientRecords = ({ patients, bills }) => {
  const { medicalRecords, birthRecords } = useData();
  const { showLoader, hideLoader } = useLoader();
  const [search, setSearch] = useState('');
  const [viewPatient, setViewPatient] = useState(null);
  const [viewPatientBills, setViewPatientBills] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [viewLabReports, setViewLabReports] = useState([]);
const [viewMedicalRecords, setViewMedicalRecords] = useState([]);
const [viewExternalLabs, setViewExternalLabs] = useState([]);
  const { apiPatients, loading, error, refetch } = useApiPatients();

  useEffect(() => {
    if (loading) {
      showLoader('Loading patients from API...');
    } else {
      hideLoader();
    }
  }, [loading]);
const [currentPage, setCurrentPage] = useState(1);
const ITEMS_PER_PAGE = 10;
  // Map API patients to local structure
  const allPatients = apiPatients.length > 0
    ? apiPatients.map(p => ({
        id: p.id,
        name: p.name,
        mrNo: p.mrNo || `MR-${p.id}`,
        fatherName: p.fatherName,
        husbandName: p.husband,
        phone: p.phone,
        cnic: p.cnic,
        age: p.age,
        bloodGroup: p.bloodGroup,
        address: p.address,
        room: p.room,
        admissionDate: p.admissionDate ? new Date(p.admissionDate).toLocaleDateString() : '-',
        dischargeDate: p.dischargeDate ? new Date(p.dischargeDate).toLocaleDateString() : null,
        diseases: p.diagnosis,
        procedure: p.procedure,
        doctorName: p.doctor,
        status: p.status,
        _apiTotalBill: p.totalBill || 0,
        _apiPaid: p.paid || 0,
        _apiDue: p.due || 0,
        _babyName: p.babyName,
        _babySex: p.babySex,
        _babyDOB: p.babyDOB,
        _babyWeight: p.babyWeight,
        _babyMode: p.babyMode,
        _babyCertificate: p.babyCertificate,
      }))
    : patients;

  const filteredPatients = allPatients.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.mrNo?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search)
  );
  const totalPages = Math.ceil(filteredPatients.length / ITEMS_PER_PAGE);
  const paginatedPatients = filteredPatients.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );


const handleViewPatient = async (patient) => {
    setLoadingDetail(true);
    showLoader('Loading patient details...');
    setViewPatient(patient);
    setViewPatientBills([]);
    try {
      const data = await fetchPatientById(patient.id);

      if (data.patientDetails) {
        const d = data.patientDetails;
        setViewPatient({
          ...patient,
          name: d.name || 'N/A',
          mrNo: d.mrNo || patient.mrNo || 'N/A',
          fatherName: d.fatherName || 'N/A',
          husbandName: d.husband || 'N/A',
          phone: d.phone || 'N/A',
          cnic: d.cnic || 'N/A',
          age: d.age || 'N/A',
          bloodGroup: d.bloodGroup || 'N/A',
          address: d.address || 'N/A',
          room: d.room || 'N/A',
          admissionDate: d.admissionDate ? new Date(d.admissionDate).toLocaleDateString() : 'N/A',
          dischargeDate: d.dischargeDate ? new Date(d.dischargeDate).toLocaleDateString() : null,
          diseases: d.diagnosis || 'N/A',
          procedure: d.procedure || 'N/A',
          doctorName: d.doctor || 'N/A',
          status: d.status || 'N/A',
          _apiTotalBill: d.totalBill || 0,
          _apiPaid: d.paid || 0,
          _apiDue: d.due || 0,
          // Baby from patientDetails fields
          _babyName: d.babyName || null,
          _babySex: d.babySex || 'N/A',
          _babyDOB: d.babyDOB ? new Date(d.babyDOB).toLocaleDateString() : 'N/A',
          _babyWeight: d.babyWeight || 'N/A',
          _babyMode: d.babyMode || 'N/A',
          _babyCertificate: d.babyCertificate || 'N/A',
          // Baby from babyRecord array (richer data)
          _babyRecord: data.babyRecord && data.babyRecord.length > 0 ? data.babyRecord[0] : null,
        });
      }

      if (data.patientBill) {
        setViewPatientBills(data.patientBill.filter(b => b.totalAmount > 0));
      }
    if (data.labReport) {
  setViewLabReports(data.labReport);
}
if (data.patientMedicalRecord) {
  setViewMedicalRecords(data.patientMedicalRecord);
}

      // Fetch External Lab Reports
      const EXT_API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
      const EXT_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';
      try {
        const extRes = await fetch(`${EXT_BASE_URL}/api/Patient/GetExternalLab/0/patientid/${patient.id}`, {
          headers: { accept: '*/*', 'X-API-TOKEN': EXT_API_TOKEN },
        });
        if (extRes.ok) {
          const extData = await extRes.json();
          setViewExternalLabs(Array.isArray(extData) ? extData : []);
        }
      } catch (err) {
        console.warn('Failed to load external lab reports:', err.message);
      }

    } catch (err) {
      console.error('Failed to load patient detail:', err);
    }
    setLoadingDetail(false);
        hideLoader();

  };
const openBase64Doc = (base64) => {
  if (!base64) return;
  const clean = (base64.includes(',') ? base64.split(',')[1] : base64).replace(/[\s\r\n]/g, '');
  const padded = clean + '='.repeat((4 - (clean.length % 4)) % 4);
  const CHUNK = 8192;
  const parts = [];
  for (let i = 0; i < padded.length; i += CHUNK) {
    const slice = padded.slice(i, i + CHUNK);
    const binary = atob(slice);
    const bytes = new Uint8Array(binary.length);
    for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
    parts.push(bytes);
  }
  const isPdf = clean.startsWith('JVBER');
  const mimeType = isPdf ? 'application/pdf' : 'image/jpeg';
  const url = URL.createObjectURL(new Blob(parts, { type: mimeType }));
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
  const getPatientBillInfo = (patientId) => {
    const apiPatient = apiPatients.find(p => p.id === patientId);
    if (apiPatient && (apiPatient.totalBill > 0 || apiPatient.paid > 0)) {
      return {
        totalBilled: apiPatient.totalBill || 0,
        totalPaid: apiPatient.paid || 0,
        due: apiPatient.due || 0,
        bills: []
      };
    }
    const patientBills = bills.filter(b => b.patientId === patientId);
    const totalBilled = patientBills.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0);
    const totalPaid = patientBills.reduce((sum, b) => sum + Number(b.paidAmount || 0), 0);
    return { totalBilled, totalPaid, due: totalBilled - totalPaid, bills: patientBills };
  };

  const getPatientMedicalRecords = (patientId) => medicalRecords.filter(r => r.patientId === patientId);
  const getPatientBaby = (patientId) => birthRecords.find(b => b.patientId === patientId);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Patient Records</h2>
        {loading && <span className="text-sm text-blue-500 animate-pulse">🔄 Fetching...</span>}
        {error && <span className="text-sm text-red-500">⚠️ API Error: {error} (showing local data)</span>}
        {!loading && !error && apiPatients.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-600">✅ Live API Data ({apiPatients.length} patients)</span>
            <button onClick={refetch} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄 Refresh</button>
          </div>
        )}
<input type="text" placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} className="input-field max-w-xs" />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr><th>MR No</th><th>Name</th><th>Husband/Father</th><th>Phone</th><th>Status</th><th>Billed</th><th>Paid</th><th>Due</th><th>Actions</th></tr>
            </thead>
            <tbody>
{paginatedPatients.map(patient => {
                const billInfo = getPatientBillInfo(patient.id);
                return (
                  <tr key={patient.id}>
                    <td className="font-mono text-sm">{patient.mrNo}</td>
                    <td className="font-medium">{patient.name}</td>
                    <td>{patient.husbandName || patient.fatherName}</td>
                    <td>{patient.phone}</td>
                    <td><span className={`status-badge ${patient.status === 'admitted' ? 'status-admitted' : 'status-discharged'}`}>{patient.status}</span></td>
                    <td>Rs. {billInfo.totalBilled.toLocaleString()}</td>
                    <td className="text-green-600">Rs. {billInfo.totalPaid.toLocaleString()}</td>
                    <td className={billInfo.due > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>Rs. {billInfo.due.toLocaleString()}</td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => handleViewPatient(patient)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">View</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
     </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t rounded-b-lg">
          <p className="text-sm text-gray-500">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredPatients.length)} of {filteredPatients.length} patients
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              title="First page"
            >⏮</button>
            <button
              onClick={() => setCurrentPage(p => p - 1)}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Previous page"
            >◀</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...'
                  ? <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                  : <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1 rounded text-sm font-medium ${currentPage === p ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    >{p}</button>
              )
            }
            <button
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Next page"
            >▶</button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Last page"
            >⏭</button>
          </div>
        </div>
      )}

      {/* View Patient Modal */}

      {/* View Patient Modal */}
      {viewPatient && (
<div className="modal-overlay" onClick={() => { setViewPatient(null); setViewLabReports([]); setViewMedicalRecords([]); }}>
          <div className="modal-content p-6 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Patient Record - {viewPatient.mrNo}</h3>
              <div className="flex gap-2 items-center">
                <button onClick={() => printPatientRecord(viewPatient, viewPatientBills, viewMedicalRecords, viewLabReports, viewExternalLabs)} className="btn-success text-sm">🖨️ Print</button>

                <button onClick={() => { setViewPatient(null); setViewLabReports([]); setViewMedicalRecords([]); }} className="text-gray-500 text-xl">×</button>
              </div>
            </div>

            {loadingDetail && (
              <div className="text-center py-8 text-blue-500 animate-pulse">🔄 Loading patient details...</div>
            )}

            {!loadingDetail && (
              <>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">Patient Information</h4>
                    <p><strong>MR No:</strong> {viewPatient.mrNo}</p>
                    <p><strong>Name:</strong> {viewPatient.name}</p>
                    <p><strong>CNIC:</strong> {viewPatient.cnic || 'N/A'}</p>
                    <p><strong>Husband:</strong> {viewPatient.husbandName || 'N/A'}</p>
                    <p><strong>Father:</strong> {viewPatient.fatherName || 'N/A'}</p>
                    <p><strong>Age:</strong> {viewPatient.age || 'N/A'}</p>
                    <p><strong>Blood Group:</strong> {viewPatient.bloodGroup || 'N/A'}</p>
                    <p><strong>Phone:</strong> {viewPatient.phone || 'N/A'}</p>
                    <p><strong>Address:</strong> {viewPatient.address || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-2">Admission Details</h4>
                    <p><strong>Room:</strong> {viewPatient.room || 'N/A'}</p>
                    <p><strong>Admission:</strong> {viewPatient.admissionDate || 'N/A'}</p>
                    <p><strong>Discharge:</strong> {viewPatient.dischargeDate || 'Still Admitted'}</p>
                    <p><strong>Diagnosis:</strong> {viewPatient.diseases || 'N/A'}</p>
                    <p><strong>Procedure:</strong> {viewPatient.procedure || 'N/A'}</p>
                    <p><strong>Doctor:</strong> {viewPatient.doctorName || 'N/A'}</p>
                    <p><strong>Status:</strong> {viewPatient.status || 'N/A'}</p>
                  </div>
                </div>

                {/* Baby Details - from babyRecord or patientDetails */}
                {(() => {
                  const baby = viewPatient._babyRecord;
                  const hasBabyRecord = baby && baby.babyName;
                  const hasBabyFromDetails = viewPatient._babyName && viewPatient._babyName !== 'string';

                  if (!hasBabyRecord && !hasBabyFromDetails) return null;

                  return (
                    <div className="mb-4 bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-bold text-orange-800 mb-2">👶 Baby Details</h4>
                      {hasBabyRecord ? (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <p><strong>Name:</strong> {baby.babyName || 'N/A'}</p>
                          <p><strong>Sex:</strong> {baby.gender || 'N/A'}</p>
                          <p><strong>DOB:</strong> {baby.dateOfBirth ? new Date(baby.dateOfBirth).toLocaleDateString() : 'N/A'}</p>
                          <p><strong>Time:</strong> {baby.birthTime ? new Date(baby.birthTime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : 'N/A'} {baby.amPm || ''}</p>
                          <p><strong>Weight:</strong> {baby.weightKg ? `${baby.weightKg} kg` : 'N/A'}</p>
                          <p><strong>Mode:</strong> {baby.modeOfDelivery || 'N/A'}</p>
                          <p><strong>Mother:</strong> {baby.motherName || 'N/A'}</p>
                          <p><strong>Father:</strong> {baby.fatherName || 'N/A'}</p>
                          <p><strong>APGAR Score:</strong> {baby.apgarScore ?? 'N/A'}</p>
                          <p><strong>Phone:</strong> {baby.cellNo || 'N/A'}</p>
                          <p><strong>Address:</strong> {baby.address || 'N/A'}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <p><strong>Name:</strong> {viewPatient._babyName || 'N/A'}</p>
                          <p><strong>Sex:</strong> {viewPatient._babySex || 'N/A'}</p>
                          <p><strong>DOB:</strong> {viewPatient._babyDOB || 'N/A'}</p>
                          <p><strong>Weight:</strong> {viewPatient._babyWeight ? `${viewPatient._babyWeight} kg` : 'N/A'}</p>
                          <p><strong>Mode:</strong> {viewPatient._babyMode || 'N/A'}</p>
                          <p><strong>Certificate:</strong> {viewPatient._babyCertificate || 'N/A'}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Bills from API */}
                <div className="mb-4">
                  <h4 className="font-bold text-gray-800 mb-2">💰 Billing Details</h4>
                  {viewPatientBills.length > 0 ? (
                    viewPatientBills.map((bill, idx) => (
                      <div key={idx} className="border rounded-lg p-4 mb-3 bg-gray-50">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                          {bill.operation_CSec > 0 && <p><strong>Operation (C/Sec):</strong> Rs. {bill.operation_CSec.toLocaleString()}</p>}
                          {bill.operation_TAH > 0 && <p><strong>Operation (TAH):</strong> Rs. {bill.operation_TAH.toLocaleString()}</p>}
                          {bill.operation_VagHyst > 0 && <p><strong>Operation (Vag Hyst):</strong> Rs. {bill.operation_VagHyst.toLocaleString()}</p>}
                          {bill.operation_Myomectomy > 0 && <p><strong>Myomectomy:</strong> Rs. {bill.operation_Myomectomy.toLocaleString()}</p>}
                          {bill.operationFee > 0 && <p><strong>Operation Fee:</strong> Rs. {bill.operationFee.toLocaleString()}</p>}
                          {bill.anesthesiaFee > 0 && <p><strong>Anesthesia Fee:</strong> Rs. {bill.anesthesiaFee.toLocaleString()}</p>}
                          {bill.oT_LaborRoomCharges > 0 && <p><strong>OT/Labor Room:</strong> Rs. {bill.oT_LaborRoomCharges.toLocaleString()}</p>}
                          {bill.roomRent > 0 && <p><strong>Room Rent:</strong> Rs. {bill.roomRent.toLocaleString()}</p>}
                          {bill.medicineBill > 0 && <p><strong>Medicine Bill:</strong> Rs. {bill.medicineBill.toLocaleString()}</p>}
                          {bill.labTestCharges > 0 && <p><strong>Lab Tests:</strong> Rs. {bill.labTestCharges.toLocaleString()}</p>}
                          {bill.childSpecialistFee > 0 && <p><strong>Child Specialist:</strong> Rs. {bill.childSpecialistFee.toLocaleString()}</p>}
                          {bill.admission_NICU > 0 && <p><strong>NICU Admission:</strong> Rs. {bill.admission_NICU.toLocaleString()}</p>}
                          {bill.nvD_IUD_Charges > 0 && <p><strong>NVD/IUD:</strong> Rs. {bill.nvD_IUD_Charges.toLocaleString()}</p>}
                          {bill.dC_Charges > 0 && <p><strong>D&C Charges:</strong> Rs. {bill.dC_Charges.toLocaleString()}</p>}
                        </div>
                        <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-4 text-center">
                          <div><p className="text-gray-500 text-xs">Total Billed</p><p className="font-bold">Rs. {Number(bill.totalAmount).toLocaleString()}</p></div>
                          <div><p className="text-gray-500 text-xs">Paid</p><p className="font-bold text-green-600">Rs. {Number(bill.paidAmount).toLocaleString()}</p></div>
                          <div><p className="text-gray-500 text-xs">Remaining</p><p className={`font-bold ${bill.remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>Rs. {Number(bill.remainingAmount).toLocaleString()}</p></div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="bg-green-50 p-3 rounded-lg grid grid-cols-3 gap-4 text-center">
                      <div><p className="text-gray-500 text-xs">Total Billed</p><p className="font-bold">Rs. {(viewPatient._apiTotalBill || 0).toLocaleString()}</p></div>
                      <div><p className="text-gray-500 text-xs">Paid</p><p className="font-bold text-green-600">Rs. {(viewPatient._apiPaid || 0).toLocaleString()}</p></div>
                      <div><p className="text-gray-500 text-xs">Due</p><p className={`font-bold ${(viewPatient._apiDue || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>Rs. {(viewPatient._apiDue || 0).toLocaleString()}</p></div>
                    </div>
                  )}
                </div>
{/* API Medical Records */}
{viewMedicalRecords.length > 0 && (
  <div className="bg-white border border-purple-100 rounded-xl overflow-hidden">
    <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-4 py-3 border-b border-purple-100">
      <h4 className="font-bold text-purple-800 flex items-center gap-2">
        📋 Medical Records
        <span className="bg-purple-200 text-purple-700 text-xs px-2 py-0.5 rounded-full">{viewMedicalRecords.length}</span>
      </h4>
    </div>
    <div className="divide-y divide-gray-100">
      {viewMedicalRecords.map((rec, i) => (
        <div key={i} className="px-4 py-3 hover:bg-purple-50/50 transition-colors">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
           {[
  ['Type',          rec.recordType],
  ['Medication',    rec.medication],
  ['Dosage',        rec.dosage],
  ['Notes',         rec.descriptionNotes],
  ['Complications', rec.complications],
  ['Date',          rec.createdDate ? new Date(rec.createdDate).toLocaleDateString() : null],
].filter(([_, v]) => v).map(([label, value]) => (
  <div key={label}>
    <span className="text-gray-400 text-xs">{label}: </span>
    <span className="font-medium text-gray-800">{value}</span>
  </div>
))}
          </div>
          {(rec.reportFile || rec.fileData || rec.base64) && (
            <button
              onClick={() => openBase64Doc(rec.reportFile || rec.fileData || rec.base64)}
              className="mt-2 flex items-center gap-1.5 text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1.5 rounded-lg transition-all">
              📄 View Document
            </button>
          )}
        </div>
      ))}
    </div>
  </div>
)}

{/* API Lab Reports */}
{viewLabReports.length > 0 && (
  <div className="bg-white border border-teal-100 rounded-xl overflow-hidden">
    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 px-4 py-3 border-b border-teal-100">
      <h4 className="font-bold text-teal-800 flex items-center gap-2">
        🧪 Lab Reports
        <span className="bg-teal-200 text-teal-700 text-xs px-2 py-0.5 rounded-full">{viewLabReports.length}</span>
      </h4>
    </div>
    <div className="p-4 grid gap-3">
      {viewLabReports.map((report, i) => {
        const fileKey = report.reportFile || report.fileData || report.base64 || report.file || report.document;
        return (
          <div key={i} className="flex items-center justify-between bg-teal-50 border border-teal-100 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center text-xl">🧾</div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">
                  {report.testName || report.reportName || report.name || `Lab Report ${i + 1}`}
                </p>
                <p className="text-xs text-gray-500">
                  {report.createdDate ? new Date(report.createdDate).toLocaleDateString() : ''}
                  {report.labName ? ` • ${report.labName}` : ''}
                </p>
              </div>
            </div>
            {fileKey ? (
              <button
                onClick={() => openBase64Doc(fileKey)}
                className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-xs px-4 py-2 rounded-lg transition-all font-medium shadow-sm">
                👁️ View Report
              </button>
            ) : (
              <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1.5 rounded-lg">No file</span>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}
                {/* Local Medical Records */}
                {getPatientMedicalRecords(viewPatient.id).length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-bold text-gray-800 mb-2">📋 Medical Records</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Details</th></tr></thead>
                        <tbody>
                          {getPatientMedicalRecords(viewPatient.id).map(r => (
                            <tr key={r.id} className="border-t">
                              <td className="p-2">{r.date} {r.time}</td>
                              <td className="p-2">{r.type}</td>
                              <td className="p-2">{r.description || r.operationName || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

<button onClick={() => { setViewPatient(null); setViewLabReports([]); setViewMedicalRecords([]); }} className="btn-secondary w-full mt-4">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};
// ── Searchable Patient Dropdown ──────────────────────────────
const SearchablePatientSelect = ({ value, onChange, patientList, required }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? patientList.filter(p =>
        p.name?.toLowerCase().includes(query.toLowerCase()) ||
        (p.mrNo || `MR-${p.id}`)?.toLowerCase().includes(query.toLowerCase())
      )
    : patientList;

  const selected = patientList.find(p => String(p.id) === String(value));

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className={selected ? 'text-gray-900 truncate' : 'text-gray-400'}>
          {selected ? `${selected.name} (${selected.mrNo || 'MR-' + selected.id})` : '-- Select Patient --'}
        </span>
        <span className="text-gray-400 text-xs flex-shrink-0">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-[9999] w-full bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <input
              autoFocus
              type="text"
              placeholder="🔍 Search name or MR No..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 260 }}>
            <div
              className="px-4 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
              onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
            >-- Select Patient --</div>
            {filtered.length === 0 && (
              <div className="px-4 py-4 text-sm text-gray-400 text-center">No patients found</div>
            )}
            {filtered.map(p => (
              <div
                key={p.id}
                onClick={() => { onChange(String(p.id)); setOpen(false); setQuery(''); }}
                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-blue-50 flex items-center justify-between gap-2
                  ${String(p.id) === String(value) ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-800'}`}
              >
                <span className="truncate">{p.name}</span>
                <span className="text-xs text-gray-400 font-mono flex-shrink-0">{p.mrNo || `MR-${p.id}`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const BillsManagement = () => {
  const { patients, bills, addBill, updateBill, addTransaction } = useData();
  const { showLoader, hideLoader } = useLoader();
  const { apiPatients } = useApiPatients();
  const { apiBills, loading: billsLoading, error: billsError, refetch: refetchBills } = useApiBills();

  const predefinedItems = [
    { id: 1,  name: 'Operation (C/Sec)',                 apiField: 'operation_CSec' },
    { id: 2,  name: 'Operation (TAH)',                   apiField: 'operation_TAH' },
    { id: 3,  name: 'Operation (Vag Hyst)',              apiField: 'operation_VagHyst' },
    { id: 4,  name: 'Operation (Myomectomy)',            apiField: 'operation_Myomectomy' },
    { id: 5,  name: 'Operation (Ap Repair & Laprotomy)', apiField: 'operation_APRepair_Laparotomy' },
    { id: 6,  name: 'Operation (NVD/IUD)',               apiField: 'operation_NVD_IUD' },
    { id: 7,  name: 'Operation Fee',                     apiField: 'operationFee' },
    { id: 8,  name: 'Anesthesia Fee',                    apiField: 'anesthesiaFee' },
    { id: 9,  name: 'O.T / Labor Room Charges',          apiField: 'oT_LaborRoomCharges' },
    { id: 10, name: 'N.V.D / I.U.D Charges',            apiField: 'nvD_IUD_Charges' },
    { id: 11, name: 'D & C Charges',                     apiField: 'dC_Charges' },
    { id: 12, name: 'Room Rent @ Rs: 5000/- per day',    apiField: 'roomRent' },
    { id: 13, name: 'Child Specialist Fee',              apiField: 'childSpecialistFee' },
    { id: 14, name: 'Medicine Bill',                     apiField: 'medicineBill' },
    { id: 15, name: 'Admission in N.I.C.U',              apiField: 'admission_NICU' },
    { id: 16, name: 'Daycare in N.I.C.U',                apiField: 'daycare_NICU' },
   { id: 17, name: 'Lab Test Charges',                  apiField: 'labTestCharges' },
  ];

  useEffect(() => {
    if (billsLoading) {
      showLoader('Loading bills...');
    } else {
      hideLoader();
    }
  }, [billsLoading]);

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [viewBill, setViewBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentUpdating, setPaymentUpdating] = useState(false);
  const [manualItems, setManualItems] = useState([]);
  const [newManualItem, setNewManualItem] = useState({ name: '', amount: '' });
  const [advanceForm, setAdvanceForm] = useState({ patientId: '', amount: '', paymentMethod: 'Cash', notes: '' });
  const [submittingBill, setSubmittingBill] = useState(false);
  const [billSubmitMsg, setBillSubmitMsg] = useState('');
  const [editBillForm, setEditBillForm] = useState(null);
  const [editBillItems, setEditBillItems] = useState(predefinedItems.map(item => ({ ...item, amount: '', selected: false })));
  const [editManualItems, setEditManualItems] = useState([]);
  const [editNewManualItem, setEditNewManualItem] = useState({ name: '', amount: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editSubmitMsg, setEditSubmitMsg] = useState('');

const [dropdownPatients, setDropdownPatients] = useState([]);

const refreshDropdown = () => {
  getPatientDropdown()
    .then(data => { if (Array.isArray(data) && data.length > 0) setDropdownPatients(data); })
    .catch(() => {});
};

useEffect(() => {
  refreshDropdown();
}, []);

const patientList = dropdownPatients.length > 0
  ? dropdownPatients
  : (apiPatients.length > 0 ? apiPatients : patients);
  const [form, setForm] = useState({
    patientId: '', room: '', operation: '',
    items: predefinedItems.map(item => ({ ...item, amount: '', selected: false })),
    advance: '', discount: '', returnAmount: ''
  });

  // Convert API bills to display format
  const displayBills = apiBills.length > 0
    ? apiBills.map(b => {
        const patient = patientList.find(p => 
  String(p.id) === String(b.patientId) || 
  p.mrNo === b.patientMRNo
);

// Extract name from patientMRNo e.g. "MR-1549 KHADIJA" → "KHADIJA"
const extractedName = b.patientMRNo 
  ? b.patientMRNo.replace(/^MR-\d+\s*/i, '').trim() 
  : '';
        // Build items list from non-zero fields
        const items = predefinedItems
          .filter(pi => b[pi.apiField] > 0)
          .map(pi => ({ description: pi.name, amount: b[pi.apiField] }));

        return {
          id: b.id,
          _apiId: b.id,
          _apiRaw: b,
          date: b.billDate ? new Date(b.billDate).toLocaleDateString() : '-',
          mrNo: b.patientMRNo ? b.patientMRNo.match(/MR-\d+/i)?.[0] || b.patientMRNo : `MR-${b.patientId}`,
          patientName: patient ? patient.name : (extractedName || `Patient #${b.patientId}`),
          room: b.roomNo,
          items: items.length > 0 ? items : [{ description: 'Bill', amount: b.totalAmount }],
          totalAmount: b.totalAmount || 0,
          paidAmount: b.paidAmount || 0,
          discount: b.discount || 0,
          remainingAmount: b.remainingAmount || 0,
status: (!b.status || b.status === 'string') ? (b.paidAmount > 0 && b.totalAmount === 0 ? 'advance' : b.remainingAmount > 0 ? 'unpaid' : 'paid') : b.status,          isAdvance: false,
        };
      })
    : bills;

  const filteredBills = displayBills.filter(b =>
    b.patientName?.toLowerCase().includes(search.toLowerCase()) ||
    b.mrNo?.toLowerCase().includes(search.toLowerCase())
  );

  const getPatientAdvance = (patientId) => {
    return bills.filter(b => b.patientId === patientId && b.isAdvance)
      .reduce((sum, b) => sum + Number(b.paidAmount || 0), 0);
  };

 const handleAdvanceSubmit = async (e) => {
  e.preventDefault();
  const patient = patientList.find(p => String(p.id) === String(advanceForm.patientId));
  if (!patient) { alert('Please select a patient'); return; }
  if (!advanceForm.amount || Number(advanceForm.amount) <= 0) { alert('Please enter a valid amount'); return; }

  showLoader('Recording advance payment...');

  const apiPayload = {
    id: 0,
    patientId: Number(advanceForm.patientId),
    patientMRNo: patient.mrNo || `MR-${patient.id}`,
    roomNo: '',
    billDate: new Date().toISOString(),
    operation_CSec: 0,
    operation_TAH: 0,
    operation_VagHyst: 0,
    operation_Myomectomy: 0,
    operation_APRepair_Laparotomy: 0,
    operation_NVD_IUD: 0,
    operationFee: 0,
    anesthesiaFee: 0,
    oT_LaborRoomCharges: 0,
    nvD_IUD_Charges: 0,
    dC_Charges: 0,
    roomRent: 0,
    childSpecialistFee: 0,
    medicineBill: 0,
    admission_NICU: 0,
    daycare_NICU: 0,
    labTestCharges: 0,
    totalAmount: 0,
    discount: 0,
    paidAmount: Number(advanceForm.amount),
    remainingAmount: 0,
    status: 'advance',
    createdOn: new Date().toISOString(),
  };

  try {
    await createPatientBill(apiPayload);
    addTransaction({
      type: 'income',
      category: 'Advance Payment',
      description: `Advance from ${patient.name} (${patient.mrNo || 'MR-' + patient.id}) - ${advanceForm.paymentMethod}${advanceForm.notes ? ' - ' + advanceForm.notes : ''}`,
      amount: Number(advanceForm.amount)
    });
   alert(`✅ Advance payment of Rs. ${Number(advanceForm.amount).toLocaleString()} recorded for ${patient.name}`);
    refetchBills();
    refreshDropdown();
  } catch (err) {
    alert(`⚠️ API failed: ${err.message}. Recorded locally.`);
    addBill({
      patientId: advanceForm.patientId,
      patientName: patient.name,
      mrNo: patient.mrNo || `MR-${patient.id}`,
      description: 'Advance Payment',
      items: [{ description: 'Advance Payment', amount: 0 }],
      totalAmount: 0,
      paidAmount: Number(advanceForm.amount),
      paymentMethod: advanceForm.paymentMethod,
      notes: advanceForm.notes,
      isAdvance: true,
      status: 'advance'
    });
  }

  hideLoader();
  setAdvanceForm({ patientId: '', amount: '', paymentMethod: 'Cash', notes: '' });
  setShowAdvanceForm(false);
};
const handleOpenEditBill = (bill) => {
    // Pre-fill predefined items from the bill's API raw data
    const raw = bill._apiRaw || {};
    const prefilledItems = predefinedItems.map(item => {
      const val = raw[item.apiField] || 0;
      return { ...item, amount: val > 0 ? String(val) : '', selected: val > 0 };
    });
    setEditBillItems(prefilledItems);
    setEditManualItems([]);
    setEditBillForm(bill);
    setEditSubmitMsg('');
    setViewBill(null); // close simple view modal
  };

  const calculateEditTotals = () => {
    const predefinedTotal = editBillItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const manualTotal = editManualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    return predefinedTotal + manualTotal;
  };

  const handleEditItemAmountChange = (id, amount) => {
    setEditBillItems(prev => prev.map(item =>
      item.id === id ? { ...item, amount, selected: amount !== '' && Number(amount) > 0 } : item
    ));
  };

  const addEditManualItem = () => {
    if (editNewManualItem.name && editNewManualItem.amount) {
      setEditManualItems(prev => [...prev, { ...editNewManualItem, id: Date.now() }]);
      setEditNewManualItem({ name: '', amount: '' });
    }
  };

  const handleEditBillSubmit = async (e) => {
    e.preventDefault();
    if (!editBillForm) return;

    const newTotal = calculateEditTotals();
    const existingPaid = Number(editBillForm.paidAmount || 0);
    const discount = Number(editBillForm.discount || 0);
    const newRemaining = newTotal - existingPaid - discount;

    setEditSubmitting(true);
    showLoader('Updating bill...');

    const raw = editBillForm._apiRaw || {};
    const updatePayload = {
      ...raw,
      id: editBillForm._apiId,
      totalAmount: newTotal,
      discount: discount,
      paidAmount: existingPaid,
      remainingAmount: newRemaining < 0 ? 0 : newRemaining,
      status: newRemaining <= 0 ? 'paid' : 'unpaid',
    };

    // Set all predefined item amounts
    editBillItems.forEach(item => {
      updatePayload[item.apiField] = item.selected && Number(item.amount) > 0 ? Number(item.amount) : 0;
    });

    try {
      await updatePatientBill(updatePayload);
      setEditSubmitMsg(`✅ Bill updated! Total: Rs. ${newTotal.toLocaleString()} | Paid: Rs. ${existingPaid.toLocaleString()} | Due: Rs. ${Math.max(0, newRemaining).toLocaleString()}`);
      refetchBills();
      setTimeout(() => {
        setEditBillForm(null);
        setEditSubmitMsg('');
      }, 2500);
    } catch (err) {
      setEditSubmitMsg(`⚠️ Failed: ${err.message}`);
    }

    setEditSubmitting(false);
    hideLoader();
  };
  const handleItemAmountChange = (id, amount) => {
    setForm({
      ...form,
      items: form.items.map(item =>
        item.id === id ? { ...item, amount, selected: amount !== '' && Number(amount) > 0 } : item
      )
    });
  };

  const addManualItem = () => {
    if (newManualItem.name && newManualItem.amount) {
      setManualItems([...manualItems, { ...newManualItem, id: Date.now() }]);
      setNewManualItem({ name: '', amount: '' });
    }
  };

  const removeManualItem = (id) => setManualItems(manualItems.filter(item => item.id !== id));

  const calculateTotals = () => {
    const predefinedTotal = form.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const manualTotal = manualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalAmount = predefinedTotal + manualTotal;
    const advance = Number(form.advance || 0);
    const discount = Number(form.discount || 0);
    const returnAmt = Number(form.returnAmount || 0);
    const balance = totalAmount - advance - discount - returnAmt;
    return { totalAmount, advance, discount, returnAmt, balance };
  };

  const resetForm = () => {
    setForm({
      patientId: '', room: '', operation: '',
      items: predefinedItems.map(item => ({ ...item, amount: '', selected: false })),
      advance: '', discount: '', returnAmount: ''
    });
    setManualItems([]);
    setShowForm(false);
    setBillSubmitMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const patient = patientList.find(p => String(p.id) === String(form.patientId));
    const selectedItems = form.items.filter(item => item.selected && Number(item.amount) > 0);
    const allItems = [
      ...selectedItems.map(item => ({ description: item.name, amount: item.amount })),
      ...manualItems.map(item => ({ description: item.name, amount: item.amount }))
    ];
    const totals = calculateTotals();
    if (allItems.length === 0) { alert('Please add at least one item with amount'); return; }

    setSubmittingBill(true);
    setBillSubmitMsg('');
        showLoader('Creating bill...');


    const apiPayload = {
      id: 0,
      patientId: Number(form.patientId),
      patientMRNo: patient?.mrNo || `MR-${form.patientId}`,
      roomNo: form.room || '',
      billDate: new Date().toISOString(),
      operation_CSec: 0, operation_TAH: 0, operation_VagHyst: 0,
      operation_Myomectomy: 0, operation_APRepair_Laparotomy: 0, operation_NVD_IUD: 0,
      operationFee: 0, anesthesiaFee: 0, oT_LaborRoomCharges: 0,
      nvD_IUD_Charges: 0, dC_Charges: 0, roomRent: 0,
      childSpecialistFee: 0, medicineBill: 0, admission_NICU: 0,
      daycare_NICU: 0, labTestCharges: 0,
     totalAmount: totals.totalAmount,
discount: totals.discount,
paidAmount: totals.advance,
remainingAmount: totals.balance,
customAmount: manualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
status: totals.balance <= 0 ? 'paid' : 'unpaid',
createdOn: new Date().toISOString(),
    };
    form.items.forEach(item => {
      if (item.selected && Number(item.amount) > 0) {
        apiPayload[item.apiField] = Number(item.amount);
      }
    });

    try {
      const result = await createPatientBill(apiPayload);
      setBillSubmitMsg(`✅ Bill saved! Bill ID: ${result.billId}`);
      addBill({
        patientId: form.patientId, patientName: patient?.name,
        mrNo: patient?.mrNo || `MR-${form.patientId}`, room: form.room,
        items: allItems, totalAmount: totals.totalAmount, advance: totals.advance,
        discount: totals.discount, returnAmount: totals.returnAmt,
        balance: totals.balance, paidAmount: totals.advance,
        status: totals.balance <= 0 ? 'paid' : 'unpaid'
      });
      refetchBills();
      refreshDropdown();
      setTimeout(resetForm, 2000);
    } catch (err) {
      setBillSubmitMsg(`⚠️ API failed: ${err.message}. Saved locally.`);
      addBill({
        patientId: form.patientId, patientName: patient?.name,
        mrNo: patient?.mrNo || `MR-${form.patientId}`, room: form.room,
        items: allItems, totalAmount: totals.totalAmount, advance: totals.advance,
        discount: totals.discount, returnAmount: totals.returnAmt,
        balance: totals.balance, paidAmount: totals.advance,
        status: totals.balance <= 0 ? 'paid' : 'unpaid'
      });
      refreshDropdown();
      setTimeout(resetForm, 2000);
    }
    setSubmittingBill(false);
        hideLoader();

  };

  // Record payment using update API
const handlePayment = async () => {
  if (!viewBill || !paymentAmount) return;
  const amount = Number(paymentAmount);
  const newPaid = Number(viewBill.paidAmount || 0) + amount;
  const newRemaining = Number(viewBill.totalAmount) - Number(viewBill.discount || 0) - newPaid;
  const newStatus = newRemaining <= 0 ? 'paid' : 'partial';

  setPaymentUpdating(true);
  showLoader('Recording payment...');

  if (viewBill._apiId) {
    const raw = viewBill._apiRaw;

    // Find the correct patientId from the patient list
    const matchedPatient = patientList.find(
      p => p.mrNo === viewBill.mrNo || String(p.id) === String(raw?.patientId)
    );
    const correctPatientId = matchedPatient?.id || raw?.patientId || 0;

    const updatePayload = {
  ...raw,
  id: editBillForm._apiId,
  totalAmount: newTotal,
  discount: discount,
  paidAmount: existingPaid,
  remainingAmount: newRemaining < 0 ? 0 : newRemaining,
  customAmount: editManualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
  status: newRemaining <= 0 ? 'paid' : 'unpaid',
};

    try {
      await updatePatientBill(updatePayload);
      refetchBills();
    } catch (err) {
      alert(`API update failed: ${err.message}`);
    }
  }

  updateBill(viewBill.id, { paidAmount: newPaid, status: newStatus });
  addTransaction({
    type: 'income',
    category: 'Patient Bill',
    description: `Payment from ${viewBill.patientName}`,
    amount
  });

  setPaymentUpdating(false);
  hideLoader();
  setPaymentAmount('');
  setViewBill(null);
};
  const printBill = async (bill) => {
    // Open window FIRST (synchronously) so popup blockers don't kill it
    const printWindow = window.open('', '_blank');

    // Resolve patientId from bill (works for both API bills and local bills)
    const patientId = bill._apiRaw?.patientId ?? bill.patientId ?? 0;

    // Fetch External Lab Reports for this patient
    let externalLabs = [];
    const EXT_API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
    const EXT_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';
    try {
      const extRes = await fetch(`${EXT_BASE_URL}/api/Patient/GetExternalLab/0/patientid/${patientId}`, {
        headers: { accept: '*/*', 'X-API-TOKEN': EXT_API_TOKEN },
      });
      if (extRes.ok) {
        const extData = await extRes.json();
        externalLabs = Array.isArray(extData) ? extData : [];
      }
    } catch (err) {
      console.warn('Failed to load external lab reports for print:', err.message);
    }

    // Build External Lab Reports HTML block
    const externalLabHTML = externalLabs.length > 0 ? `
      <div style="margin-top:20px;">
        <div style="font-size:14px;font-weight:bold;color:#1e40af;border-bottom:2px solid #1e40af;padding-bottom:5px;margin-bottom:10px;">
          🧪 External Lab Reports
        </div>
        ${externalLabs.map(ext => `
          <table style="width:100%;border-collapse:collapse;margin-bottom:12px;font-size:11px;">
            <tr>
              <td colspan="2" style="padding:5px 8px;font-weight:bold;background:#eff6ff;border:1px solid #ddd;color:#1e40af;">
                Date: ${ext.testDate || '-'} ${ext.ageSex ? '&nbsp;|&nbsp; Age/Sex: ' + ext.ageSex : ''} ${ext.patientName ? '&nbsp;|&nbsp; ' + ext.patientName : ''}
              </td>
            </tr>
            ${[
              ['HBsAg', ext.hBsAg], ['HCV', ext.hcv], ['Blood Sugar (Random)', ext.bloodSugarRandom],
              ['Blood Group', ext.bloodGroup], ['Rh Factor', ext.rhFactor], ['ALT (SGPT)', ext.alT_SGPT],
              ['ALP', ext.alp], ['Bilirubin (Total)', ext.bilirubinTotal], ['Urea', ext.urea],
              ['Creatinine', ext.creatinine], ['Uric Acid', ext.uricAcid], ['PT (Prothrombin Time)', ext.pT_ProthrombinTime],
              ['APTT', ext.aptt], ['Hemoglobin', ext.hemoglobin], ['Total WBC Count', ext.totalWBCCount],
              ['Platelet Count', ext.plateletCount],
            ].filter(([, v]) => v).map(([label, val]) => `
              <tr>
                <td style="padding:4px 8px;border:1px solid #ddd;width:180px;color:#555;">${label}</td>
                <td style="padding:4px 8px;border:1px solid #ddd;">${val}</td>
              </tr>
            `).join('')}
          </table>
        `).join('')}
      </div>
    ` : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill - ${bill.mrNo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 10px; margin-bottom: 15px; }
          .clinic-name { font-size: 18px; font-weight: bold; color: #1e40af; }
          .address { font-size: 10px; color: #666; }
          .invoice-info { display: flex; justify-content: space-between; margin-bottom: 15px; padding: 10px; background: #f5f5f5; }
          .title { font-size: 16px; font-weight: bold; text-align: center; margin: 10px 0; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #1e40af; color: white; }
          .amount { text-align: right; }
          .total-row { font-weight: bold; background: #e3f2fd; }
          .summary { background: #f5f5f5; padding: 15px; margin-top: 15px; }
          .summary-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #ddd; }
          .summary-row:last-child { border-bottom: none; }
          .balance { font-size: 16px; font-weight: bold; color: ${(Number(bill.remainingAmount || 0)) > 0 ? '#dc2626' : '#16a34a'}; }
          .signature { display: flex; justify-content: space-between; margin-top: 50px; }
          .sig-box { text-align: center; }
          .sig-line { border-top: 1px solid #000; width: 120px; margin-bottom: 5px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="clinic-name">${DOCTOR_INFO.clinicName}</div>
          <div class="address">${DOCTOR_INFO.address}</div>
          <div class="address">Phone: ${DOCTOR_INFO.phone}</div>
        </div>
        <div class="title">INVOICE / BILL</div>
        <div class="invoice-info">
          <div>
            <p><strong>Patient:</strong> ${bill.patientName}</p>
            <p><strong>MR No:</strong> ${bill.mrNo}</p>
            ${bill.room ? `<p><strong>Room:</strong> ${bill.room}</p>` : ''}
          </div>
          <div style="text-align:right;">
            <p><strong>Date:</strong> ${bill.date}</p>
            <p><strong>Status:</strong> ${bill.status?.toUpperCase()}</p>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:50px;">S.No</th>
              <th>Particulars</th>
              <th style="width:120px;" class="amount">Amount (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            ${bill.items?.map((item, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${item.description}</td>
                <td class="amount">${Number(item.amount).toLocaleString()}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="2"><strong>Total Amount</strong></td>
              <td class="amount"><strong>Rs. ${Number(bill.totalAmount).toLocaleString()}</strong></td>
            </tr>
          </tbody>
        </table>
        <div class="summary">
          <div class="summary-row"><span>Total Amount:</span><span>Rs. ${Number(bill.totalAmount).toLocaleString()}</span></div>
          ${bill.discount ? `<div class="summary-row"><span>Discount:</span><span>Rs. ${Number(bill.discount).toLocaleString()}</span></div>` : ''}
          <div class="summary-row"><span>Amount Paid:</span><span style="color:#16a34a;">Rs. ${Number(bill.paidAmount || 0).toLocaleString()}</span></div>
        <div class="summary-row"><span><strong>Balance Due:</strong></span><span class="balance">Rs. ${Number(bill.remainingAmount || 0).toLocaleString()}</span></div>
        </div>
        ${externalLabHTML}
        <div class="signature">
          <div class="sig-box"><div class="sig-line"></div><div>Patient/Attendant</div></div>
          <div class="sig-box"><div class="sig-line"></div><div>Cashier</div></div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">Bills Management</h2>
          {billsLoading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {billsError && <span className="text-xs text-red-500">⚠️ API Error</span>}
          {!billsLoading && !billsError && apiBills.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-600">✅ Live API ({apiBills.filter(b => b.totalAmount > 0).length} bills)</span>
              <button onClick={refetchBills} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="input-field max-w-xs" />
          <button onClick={() => setShowAdvanceForm(true)} className="btn-success text-sm">💰 Add Advance</button>
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Create Bill</button>
        </div>
      </div>

      {/* Advance Payment Form */}
      {showAdvanceForm && (
        <div className="card p-6 border-2 border-green-400">
          <h3 className="font-bold text-green-800 mb-4">💰 Record Advance Payment</h3>
          <form onSubmit={handleAdvanceSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Patient *</label>
             <SearchablePatientSelect
  value={advanceForm.patientId}
  onChange={(val) => setAdvanceForm({ ...advanceForm, patientId: val })}
  patientList={patientList}
  required
/>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Advance Amount (Rs.) *</label>
                <input type="number" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} className="input-field" min="1" required />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Method</label>
                <select value={advanceForm.paymentMethod} onChange={(e) => setAdvanceForm({ ...advanceForm, paymentMethod: e.target.value })} className="input-field">
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="JazzCash/Easypaisa">JazzCash/Easypaisa</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                <input type="text" value={advanceForm.notes} onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })} className="input-field" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-success text-sm">💰 Record Advance</button>
              <button type="button" onClick={() => { setShowAdvanceForm(false); setAdvanceForm({ patientId: '', amount: '', paymentMethod: 'Cash', notes: '' }); }} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Create Bill Form */}
      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4 text-center text-lg border-b pb-2">Create New Bill - Dr. Ruqqia Sultana Clinic & Maternity Home</h3>
          {billSubmitMsg && (
            <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${billSubmitMsg.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {billSubmitMsg}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Patient *</label>
              <SearchablePatientSelect
  value={form.patientId}
  onChange={(val) => setForm({ ...form, patientId: val })}
  patientList={patientList}
  required
/>
{dropdownPatients.length > 0 && <p className="text-xs text-green-600 mt-1">✅ {dropdownPatients.length} patients loaded</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Room #</label>
                <input type="text" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input type="text" value={new Date().toLocaleDateString()} className="input-field bg-gray-50" readOnly />
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-3 py-2 text-left border-b w-12">S.No</th>
                    <th className="px-3 py-2 text-left border-b">Particulars</th>
                    <th className="px-3 py-2 text-right border-b w-32">Rupees</th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, index) => (
                    <tr key={item.id} className={item.selected ? 'bg-green-50' : ''}>
                      <td className="px-3 py-2 border-b text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 border-b">{item.name}</td>
                      <td className="px-3 py-2 border-b">
                        <input type="number" placeholder="0" value={item.amount} onChange={(e) => handleItemAmountChange(item.id, e.target.value)} className="input-field text-right w-full" />
                      </td>
                    </tr>
                  ))}
                  {manualItems.map((item, index) => (
                    <tr key={item.id} className="bg-yellow-50">
                      <td className="px-3 py-2 border-b text-gray-500">{form.items.length + index + 1}</td>
                      <td className="px-3 py-2 border-b">{item.name} <span className="text-xs text-yellow-600">(Custom)</span></td>
                      <td className="px-3 py-2 border-b flex items-center gap-1">
                        <span className="text-right flex-1">Rs. {Number(item.amount).toLocaleString()}</span>
                        <button type="button" onClick={() => removeManualItem(item.id)} className="text-red-500 hover:text-red-700">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-2">➕ Add Custom Item (Not in list)</h4>
            <div className="flex gap-2">
  <input type="text" placeholder="Item name..." value={newManualItem.name} onChange={(e) => setNewManualItem({ ...newManualItem, name: e.target.value })} className="input-field" style={{flex: 3}} />
  <input type="number" placeholder="Amount" value={newManualItem.amount} onChange={(e) => setNewManualItem({ ...newManualItem, amount: e.target.value })} className="input-field" style={{flex: 1, minWidth: '80px', maxWidth: '120px'}} />
  <button type="button" onClick={addManualItem} className="btn-success text-sm whitespace-nowrap">Add</button>
</div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-bold">Total Amount</span>
                <span className="font-bold text-lg">Rs. {totals.totalAmount.toLocaleString()}</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Advance</label><input type="number" placeholder="0" value={form.advance} onChange={(e) => setForm({ ...form, advance: e.target.value })} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Discount</label><input type="number" placeholder="0" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Return</label><input type="number" placeholder="0" value={form.returnAmount} onChange={(e) => setForm({ ...form, returnAmount: e.target.value })} className="input-field" /></div>
              </div>
              <div className="flex justify-between items-center py-2 border-t mt-2">
                <span className="font-bold text-lg">Balance Due</span>
                <span className={`font-bold text-xl ${totals.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>Rs. {totals.balance.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" disabled={submittingBill} className={`btn-primary text-sm ${submittingBill ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {submittingBill ? '⏳ Saving...' : 'Create Bill'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Bills Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Date</th><th>MR No</th><th>Patient</th><th>Total</th><th>Paid</th><th>Due</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredBills.map(bill => (
                <tr key={`${bill._apiId || ''}-${bill.id}`}>
                  <td>{bill.date}</td>
                  <td className="font-mono text-sm">{bill.mrNo}</td>
                  <td className="font-medium">{bill.patientName}</td>
                  <td>Rs. {Number(bill.totalAmount).toLocaleString()}</td>
                  <td className="text-green-600 font-medium">Rs. {Number(bill.paidAmount || 0).toLocaleString()}</td>
                  <td className={Number(bill.remainingAmount || 0) > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
                    Rs. {Number(bill.remainingAmount || 0).toLocaleString()}
                  </td>
                  <td><span className={`status-badge status-${bill.status}`}>{bill.status}</span></td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => setViewBill(bill)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">View</button>
                      <button onClick={() => printBill(bill)} className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200">🖨️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredBills.length === 0 && !billsLoading && (
                <tr><td colSpan="8" className="text-center py-8 text-gray-500">No bills found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View/Edit Bill Modal */}
      {viewBill && (
        <div className="modal-overlay" onClick={() => { setViewBill(null); setPaymentAmount(''); }}>
          <div className="modal-content p-6 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Bill Details - {viewBill.mrNo}</h3>
              <div className="flex gap-2">
                <button onClick={() => printBill(viewBill)} className="btn-success text-sm">🖨️ Print</button>
                <button onClick={() => { setViewBill(null); setPaymentAmount(''); }} className="text-gray-500 text-2xl leading-none">×</button>
              </div>
            </div>

            {/* Patient & Bill Info */}
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg text-sm space-y-1">
                <p><strong>Patient:</strong> {viewBill.patientName}</p>
                <p><strong>MR No:</strong> {viewBill.mrNo}</p>
                {viewBill.room && <p><strong>Room:</strong> {viewBill.room}</p>}
                <p><strong>Date:</strong> {viewBill.date}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1">
                <p><strong>Status:</strong> <span className={`px-2 py-0.5 rounded text-xs font-medium ${viewBill.status === 'paid' ? 'bg-green-100 text-green-700' : viewBill.status === 'advance' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{viewBill.status?.toUpperCase()}</span></p>
                {viewBill._apiId && <p><strong>Bill ID:</strong> #{viewBill._apiId}</p>}
              </div>
            </div>

            {/* Bill Items */}
            <div className="border rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-3 py-2 text-left border-b w-12">S.No</th>
                    <th className="px-3 py-2 text-left border-b">Particulars</th>
                    <th className="px-3 py-2 text-right border-b w-36">Amount (Rs.)</th>
                  </tr>
                </thead>
                <tbody>
                  {viewBill.items?.map((item, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 border-b text-gray-500">{i + 1}</td>
                      <td className="px-3 py-2 border-b">{item.description}</td>
                      <td className="px-3 py-2 border-b text-right font-medium">
                        {Number(item.amount) > 0 ? `Rs. ${Number(item.amount).toLocaleString()}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-xs text-gray-500 mb-1">Total Billed</p>
                  <p className="font-bold text-gray-900">Rs. {Number(viewBill.totalAmount || 0).toLocaleString()}</p>
                </div>
                {viewBill.discount > 0 && (
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500 mb-1">Discount</p>
                    <p className="font-bold text-orange-600">Rs. {Number(viewBill.discount || 0).toLocaleString()}</p>
                  </div>
                )}
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-xs text-gray-500 mb-1">Amount Paid</p>
                  <p className="font-bold text-green-600">Rs. {Number(viewBill.paidAmount || 0).toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-xs text-gray-500 mb-1">Balance Due</p>
                  <p className={`font-bold ${Number(viewBill.remainingAmount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    Rs. {Number(viewBill.remainingAmount || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Section - always show if not fully paid */}
            {viewBill.status !== 'paid' && (
              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50 mb-4">
                <h4 className="font-bold text-green-800 mb-3">💰 Record Payment</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Amount (Rs.) *</label>
                    <input
                      type="number"
                      placeholder="Enter amount..."
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="input-field"
                      min="1"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handlePayment}
                      disabled={paymentUpdating || !paymentAmount}
                      className={`btn-success w-full ${paymentUpdating || !paymentAmount ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {paymentUpdating ? '⏳ Recording...' : '✅ Record Payment'}
                    </button>
                  </div>
                </div>

                {/* Quick amount buttons */}
                {Number(viewBill.remainingAmount || 0) > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500 self-center">Quick:</span>
                    {[500, 1000, 2000, 5000].map(amt => (
                      <button
                        key={amt}
                        onClick={() => setPaymentAmount(String(amt))}
                        className="text-xs px-3 py-1 bg-white border border-green-300 text-green-700 rounded-full hover:bg-green-100"
                      >
                        Rs. {amt.toLocaleString()}
                      </button>
                    ))}
                    <button
                      onClick={() => setPaymentAmount(String(Math.max(0, Number(viewBill.remainingAmount || 0))))}
                      className="text-xs px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-700"
                    >
                      Full Due: Rs. {Number(viewBill.remainingAmount || 0).toLocaleString()}
                    </button>
                  </div>
                )}
              </div>
            )}

            {viewBill.status === 'paid' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-center">
                <p className="text-green-700 font-bold text-lg">✅ Bill Fully Paid</p>
                <p className="text-green-600 text-sm">Total paid: Rs. {Number(viewBill.paidAmount || 0).toLocaleString()}</p>
              </div>
            )}
{/* Open full edit form for advance bills */}
            {viewBill.status === 'advance' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-center">
                <p className="text-blue-700 font-bold mb-2">💰 Advance Paid: Rs. {Number(viewBill.paidAmount || 0).toLocaleString()}</p>
                <p className="text-blue-600 text-sm mb-3">Patient has paid advance. Click below to fill the complete bill details.</p>
                <button
                  onClick={() => handleOpenEditBill(viewBill)}
                  className="btn-primary"
                >
                  📋 Fill Complete Bill Details
                </button>
              </div>
            )}
            <button onClick={() => { setViewBill(null); setPaymentAmount(''); }} className="btn-secondary w-full text-sm">Close</button>
          </div>
        </div>
      )}
  {/* Full Bill Edit Modal - for advance bills */}
      {editBillForm && (
        <div className="modal-overlay" onClick={() => setEditBillForm(null)}>
          <div className="modal-content p-6 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">📋 Fill Bill Details - {editBillForm.mrNo}</h3>
              <button onClick={() => setEditBillForm(null)} className="text-gray-500 text-2xl">×</button>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-3">
              <span className="text-2xl">💰</span>
              <div>
                <p className="font-bold text-green-800">Advance Already Paid: Rs. {Number(editBillForm.paidAmount || 0).toLocaleString()}</p>
                <p className="text-green-600 text-sm">This amount will be deducted from the total bill automatically.</p>
              </div>
            </div>

            {editSubmitMsg && (
              <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${editSubmitMsg.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {editSubmitMsg}
              </div>
            )}

            <form onSubmit={handleEditBillSubmit} className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient</label>
                  <input type="text" value={editBillForm.patientName} className="input-field bg-gray-50" readOnly />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">MR No</label>
                  <input type="text" value={editBillForm.mrNo} className="input-field bg-gray-50" readOnly />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input type="text" value={new Date().toLocaleDateString()} className="input-field bg-gray-50" readOnly />
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-3 py-2 text-left border-b w-12">S.No</th>
                      <th className="px-3 py-2 text-left border-b">Particulars</th>
                      <th className="px-3 py-2 text-right border-b w-32">Rupees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editBillItems.map((item, index) => (
                      <tr key={item.id} className={item.selected ? 'bg-green-50' : ''}>
                        <td className="px-3 py-2 border-b text-gray-500">{index + 1}</td>
                        <td className="px-3 py-2 border-b">{item.name}</td>
                        <td className="px-3 py-2 border-b">
                          <input
                            type="number"
                            placeholder="0"
                            value={item.amount}
                            onChange={(e) => handleEditItemAmountChange(item.id, e.target.value)}
                            className="input-field text-right w-full"
                          />
                        </td>
                      </tr>
                    ))}
                    {editManualItems.map((item, index) => (
                      <tr key={item.id} className="bg-yellow-50">
                        <td className="px-3 py-2 border-b text-gray-500">{editBillItems.length + index + 1}</td>
                        <td className="px-3 py-2 border-b">{item.name} <span className="text-xs text-yellow-600">(Custom)</span></td>
                        <td className="px-3 py-2 border-b flex items-center gap-1">
                          <span className="flex-1 text-right">Rs. {Number(item.amount).toLocaleString()}</span>
                          <button type="button" onClick={() => setEditManualItems(prev => prev.filter(i => i.id !== item.id))} className="text-red-500">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">➕ Add Custom Item</h4>
                <div className="flex gap-2">
                  <input type="text" placeholder="Item name..." value={editNewManualItem.name} onChange={(e) => setEditNewManualItem({ ...editNewManualItem, name: e.target.value })} className="input-field flex-1" />
                  <input type="number" placeholder="Amount" value={editNewManualItem.amount} onChange={(e) => setEditNewManualItem({ ...editNewManualItem, amount: e.target.value })} className="input-field w-24"
 />
                  <button type="button" onClick={addEditManualItem} className="btn-success text-sm">Add</button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-bold">Total Bill Amount</span>
                  <span className="font-bold text-lg">Rs. {calculateEditTotals().toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">Advance Paid</span>
                  <span className="text-green-600 font-medium">- Rs. {Number(editBillForm.paidAmount || 0).toLocaleString()}</span>
                </div>
                {Number(editBillForm.discount || 0) > 0 && (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-600">Discount</span>
                    <span className="text-orange-600 font-medium">- Rs. {Number(editBillForm.discount || 0).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-t mt-2">
                  <span className="font-bold text-lg">Balance Due</span>
                  <span className={`font-bold text-xl ${calculateEditTotals() - Number(editBillForm.paidAmount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    Rs. {Math.max(0, calculateEditTotals() - Number(editBillForm.paidAmount || 0) - Number(editBillForm.discount || 0)).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <button type="submit" disabled={editSubmitting} className={`btn-primary flex-1 ${editSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {editSubmitting ? '⏳ Saving...' : '💾 Save Bill Details'}
                </button>
                <button type="button" onClick={() => setEditBillForm(null)} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

// Finance Management - Income & Expense

// Finance Management - Income & Expense
// Finance Management - Income & Expense
const FinanceManagement = () => {
  const { transactions, addTransaction, getStats } = useData();
  const { showLoader, hideLoader } = useLoader();
  const [showForm, setShowForm] = useState(false);
  const [transactionType, setTransactionType] = useState('expense');
  const [filterType, setFilterType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [form, setForm] = useState({ category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], consumerNumber: '', billNumber: '' });
  const [apiTransactions, setApiTransactions] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [editingTransaction, setEditingTransaction] = useState(null);

  const incomeCategories = ['Patient Bill', 'Consultation Fee', 'Lab Tests', 'Procedure Fee', 'Other Income'];
  const expenseCategories = ['Salary', 'Medicine/Supplies', 'Utilities (Electricity)', 'Utilities (Gas)', 'Utilities (Water)', 'Rent', 'Maintenance', 'Equipment', 'Cleaning', 'Food/Tea', 'Stationery', 'Other Expense'];

  // Load from API on mount
  useEffect(() => {
    loadApiTransactions();
  }, []);

  const loadApiTransactions = async () => {
    setApiLoading(true);
    showLoader('Loading income/expense records...');
    try {
      const data = await fetchAllIncomeExpense();
      setApiTransactions(Array.isArray(data) ? data : []);
      setApiError('');
    } catch (err) {
      setApiError(err.message);
    }
    setApiLoading(false);
    hideLoader();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoader(editingTransaction ? 'Updating record...' : 'Saving record...');

    const apiPayload = {
id: editingTransaction ? editingTransaction._apiId : 0,
      category: form.category,
      amount: Number(form.amount),
      description: form.description || '',
      expenseDate: new Date(form.date).toISOString(),
      createdAt: new Date().toISOString(),
      type: transactionType === 'income' ? 'Income' : 'Expense',
    };

    try {
      if (editingTransaction) {
        await updateIncomeExpense(apiPayload);
        // Update local apiTransactions state
        setApiTransactions(prev => prev.map(t =>
          t.id === editingTransaction.id ? { ...t, ...apiPayload } : t
        ));
      } else {
        const result = await createIncomeExpense(apiPayload);
        // Reload to get fresh data with new ID
        await loadApiTransactions();
      }
      // Also save locally as before
      addTransaction({ type: transactionType, ...form });
    } catch (err) {
      alert(`API failed: ${err.message}. Saved locally only.`);
      addTransaction({ type: transactionType, ...form });
    }

    hideLoader();
    setForm({ category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], consumerNumber: '', billNumber: '' });
    setShowForm(false);
    setEditingTransaction(null);
  };

  // Use API transactions if available, mapped to local shape
  const mappedApiTransactions = apiTransactions.map(t => ({
    id: `api-${t.id}`,
    _apiId: t.id,
    _apiRaw: t,
    type: t.type === 'Income' ? 'income' : 'expense',
    category: t.category && t.category !== 'string' ? t.category : 'Other',
    description: t.description && t.description !== 'string' ? t.description : '',
    amount: t.amount,
    date: t.expenseDate ? t.expenseDate.split('T')[0] : '',
    consumerNumber: '',
    billNumber: '',
  }));

  const displayTransactions = mappedApiTransactions.length > 0 ? mappedApiTransactions : transactions;

  // Filter transactions
  const today = new Date().toISOString().split('T')[0];
  const thisMonth = new Date().toISOString().slice(0, 7);

  let filteredTransactions = displayTransactions;
  if (filterType !== 'all') filteredTransactions = filteredTransactions.filter(t => t.type === filterType);
  if (filterPeriod === 'today') filteredTransactions = filteredTransactions.filter(t => t.date === today);
  else if (filterPeriod === 'month') filteredTransactions = filteredTransactions.filter(t => t.date?.startsWith(thisMonth));

  const filteredIncome = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + Number(t.amount), 0);
  const filteredExpense = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount), 0);

  // Group by category
  const incomeByCategory = {};
  const expenseByCategory = {};
  filteredTransactions.forEach(t => {
    if (t.type === 'income') {
      incomeByCategory[t.category] = (incomeByCategory[t.category] || 0) + Number(t.amount);
    } else {
      expenseByCategory[t.category] = (expenseByCategory[t.category] || 0) + Number(t.amount);
    }
  });

  const handleEditTransaction = (t) => {
    if (!t._apiId) return;
    setEditingTransaction(t);
    setTransactionType(t.type);
    setForm({
      category: t.category,
      description: t.description,
      amount: String(t.amount),
      date: t.date,
      consumerNumber: t.consumerNumber || '',
      billNumber: t.billNumber || '',
    });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">Income & Expense Management</h2>
          {apiLoading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {apiError && <span className="text-xs text-red-500">⚠️ API Error: {apiError}</span>}
          {!apiLoading && !apiError && mappedApiTransactions.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-600">✅ Live API ({mappedApiTransactions.length} records)</span>
              <button onClick={loadApiTransactions} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} className="input-field">
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="month">This Month</option>
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="input-field">
            <option value="all">All Types</option>
            <option value="income">Income Only</option>
            <option value="expense">Expense Only</option>
          </select>
          <button onClick={() => { setShowForm(true); setTransactionType('income'); setEditingTransaction(null); }} className="btn-success text-sm">+ Income</button>
          <button onClick={() => { setShowForm(true); setTransactionType('expense'); setEditingTransaction(null); }} className="btn-danger text-sm">+ Expense</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card p-6 bg-green-50 border-green-200">
          <p className="text-green-600 font-medium">Total Income</p>
          <p className="text-3xl font-bold text-green-700">Rs. {filteredIncome.toLocaleString()}</p>
        </div>
        <div className="card p-6 bg-red-50 border-red-200">
          <p className="text-red-600 font-medium">Total Expenses</p>
          <p className="text-3xl font-bold text-red-700">Rs. {filteredExpense.toLocaleString()}</p>
        </div>
        <div className={`card p-6 ${filteredIncome - filteredExpense >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
          <p className={`font-medium ${filteredIncome - filteredExpense >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Balance</p>
          <p className={`text-3xl font-bold ${filteredIncome - filteredExpense >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
            Rs. {(filteredIncome - filteredExpense).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Add/Edit Transaction Form */}
      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            {editingTransaction
              ? `✏️ Edit ${transactionType === 'income' ? 'Income' : 'Expense'}`
              : transactionType === 'income' ? '💰 Add Income' : '💸 Add Expense'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category *</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-field" required>
                  <option value="">Select Category</option>
                  {(transactionType === 'income' ? incomeCategories : expenseCategories).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount (Rs.) *</label>
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-field" required />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field" placeholder="Optional details" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date *</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" required />
              </div>
            </div>
            {transactionType === 'expense' && form.category && form.category.startsWith('Utilities') && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Consumer Number</label>
                  <input type="text" value={form.consumerNumber} onChange={(e) => setForm({ ...form, consumerNumber: e.target.value })} className="input-field" placeholder="Enter consumer number" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bill Number</label>
                  <input type="text" value={form.billNumber} onChange={(e) => setForm({ ...form, billNumber: e.target.value })} className="input-field" placeholder="Enter bill number" />
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button type="submit" className={transactionType === 'income' ? 'btn-success text-sm' : 'btn-danger text-sm'}>
                {editingTransaction ? 'Update' : 'Add'} {transactionType === 'income' ? 'Income' : 'Expense'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingTransaction(null); }} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Category Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-bold text-green-700 mb-4">💰 Income by Category</h3>
          {Object.keys(incomeByCategory).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(incomeByCategory).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => (
                <div key={cat} className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">{cat}</span>
                  <span className="font-bold text-green-600">Rs. {amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500">No income records</p>}
        </div>
        <div className="card p-6">
          <h3 className="font-bold text-red-700 mb-4">💸 Expenses by Category</h3>
          {Object.keys(expenseByCategory).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(expenseByCategory).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => (
                <div key={cat} className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">{cat}</span>
                  <span className="font-bold text-red-600">Rs. {amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500">No expense records</p>}
        </div>
      </div>

      {/* Transaction List */}
      <div className="card p-6">
        <h3 className="font-bold text-gray-900 mb-4">All Transactions</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th><th>Type</th><th>Category</th><th>Description</th>
                <th>Consumer No.</th><th>Bill No.</th><th>Amount</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.slice().reverse().map(t => (
                <tr key={t.id}>
                  <td>{t.date}</td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${t.type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {t.type}
                    </span>
                  </td>
                  <td>{t.category}</td>
                  <td>{t.description || '-'}</td>
                  <td>{t.consumerNumber || '-'}</td>
                  <td>{t.billNumber || '-'}</td>
                  <td className={`font-bold ${t.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                    {t.type === 'income' ? '+' : '-'} Rs. {Number(t.amount).toLocaleString()}
                  </td>
                  <td>
                    {t._apiId && (
                      <button
                        onClick={() => handleEditTransaction(t)}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                      >
                        ✏️ Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
// Discharge Approval - Admin clears dues and approves discharge
const DischargeApproval = () => {
  const { patients, bills, medicalRecords, birthRecords, approveDischarge, cancelDischargeRequest, getPatientDues } = useData();
  const { showLoader, hideLoader } = useLoader();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [apiRequests, setApiRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  // Fetch all discharge requests from API on mount
  useEffect(() => {
    loadDischargeRequests();
  }, []);

 const loadDischargeRequests = async () => {
    setLoadingRequests(true);
    showLoader('Loading discharge requests...');
    try {
      const data = await fetchDischargeRequests();
      setApiRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Failed to load discharge requests:', err.message);
    }
    setLoadingRequests(false);
        hideLoader();

  };

  const handleAllow = async (request) => {
    if (!window.confirm(`Allow discharge for MR No: ${request.mrNo}?\n\nPatient Condition: ${request.patientCondition}`)) return;
    setUpdatingId(request.id);
    showLoader('Approving discharge...');
    try {
      await updateDischargeRequest({
        id: request.id,
        patientId: request.patientId,
        mrNo: request.mrNo,
        patientCondition: request.patientCondition,
        babyCondition: request.babyCondition || '',
        isRequestForDischarge: true,
        isAllowed: true,
        isPaymentDone: request.isPaymentDone || false,
        createdDate: request.createdDate || new Date().toISOString(),
      });
      // Also update local state
      const localPatient = patients.find(p => p.mrNo === request.mrNo || p.id === request.patientId);
      if (localPatient) approveDischarge(localPatient.id);
      await loadDischargeRequests();
      alert('✅ Discharge approved successfully!');
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
    }
    setUpdatingId(null);
        hideLoader();

  };

  const handleDisallow = async (request) => {
    if (!window.confirm(`Reject discharge request for MR No: ${request.mrNo}?`)) return;
    setUpdatingId(request.id);
    showLoader('Rejecting discharge request...');
    try {
      await updateDischargeRequest({
        id: request.id,
        patientId: request.patientId,
        mrNo: request.mrNo,
        patientCondition: request.patientCondition,
        babyCondition: request.babyCondition || '',
        isRequestForDischarge: true,
        isAllowed: false,
        isPaymentDone: false,
        createdDate: request.createdDate || new Date().toISOString(),
      });
      // Also cancel locally
      const localPatient = patients.find(p => p.mrNo === request.mrNo || p.id === request.patientId);
      if (localPatient) cancelDischargeRequest(localPatient.id);
      await loadDischargeRequests();
      alert('❌ Discharge request rejected.');
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
    }
    setUpdatingId(null);
        hideLoader();

  };

  // Local-only admitted patients (for the bottom table)
  const admittedPatients = patients.filter(p => p.status === 'admitted' && !p.dischargeRequested);

  const getPatientBillInfo = (patientId) => {
    const patientBills = bills.filter(b => b.patientId === patientId);
    const totalBilled = patientBills.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0);
    const totalPaid = patientBills.reduce((sum, b) => sum + Number(b.paidAmount || 0), 0);
    return { totalBilled, totalPaid, due: totalBilled - totalPaid };
  };

  const getPatientMedicalRecords = (patientId) => medicalRecords.filter(r => r.patientId === patientId);
  const getPatientBaby = (patientId) => birthRecords.find(b => b.patientId === patientId);

const pendingRequests = apiRequests.filter(r => r.isRequestForDischarge === true && r.isAllowed === false);
const approvedRequests = apiRequests.filter(r => r.isRequestForDischarge === true && r.isAllowed === true);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">✅ Discharge Approval</h2>
        <div className="flex items-center gap-2">
          {loadingRequests && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          <button onClick={loadDischargeRequests} className="text-xs px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-bold text-blue-800 mb-2">ℹ️ Discharge Process:</h3>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Doctor requests discharge and enters patient condition</li>
          <li>Request appears here for Admin approval</li>
          <li>Admin reviews and clicks <strong>Allow</strong> or <strong>Not Allowed</strong></li>
          <li>Allowed discharges are moved to approved section</li>
        </ol>
      </div>

      {/* Pending Discharge Requests from API */}
      <div className="card p-4 border-2 border-yellow-400">
        <h3 className="font-bold text-yellow-800 mb-4 flex items-center gap-2">
          <span className="animate-pulse">⚠️</span>
          Pending Discharge Requests ({pendingRequests.length})
        </h3>

        {pendingRequests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>MR No</th>
                  <th>Patient Condition</th>
                  <th>Baby Condition</th>
                  <th>Date</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map(req => (
                  <tr key={req.id} className="bg-yellow-50">
                    <td className="font-mono text-sm">#{req.id}</td>
                    <td className="font-mono font-medium">{req.mrNo}</td>
                    <td>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {req.patientCondition || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {req.babyCondition ? (
                        <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs">
                          {req.babyCondition}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="text-sm text-gray-500">
                      {req.createdDate && req.createdDate !== '0001-01-01T00:00:00'
                        ? new Date(req.createdDate).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs ${req.isPaymentDone ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {req.isPaymentDone ? '✅ Paid' : '❌ Pending'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAllow(req)}
                          disabled={updatingId === req.id}
                          className={`text-xs px-3 py-1 rounded font-medium ${updatingId === req.id ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                        >
                          {updatingId === req.id ? '⏳' : '✅ Allow'}
                        </button>
                        <button
                          onClick={() => handleDisallow(req)}
                          disabled={updatingId === req.id}
                          className={`text-xs px-3 py-1 rounded font-medium ${updatingId === req.id ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                        >
                          {updatingId === req.id ? '⏳' : '❌ Not Allowed'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-6 text-gray-500">
            {loadingRequests ? '🔄 Loading requests...' : '✅ No pending discharge requests'}
          </p>
        )}
      </div>


      {/* All Admitted Patients (local) */}
      <div className="card p-4">
        <h3 className="font-bold text-gray-900 mb-4">All Admitted Patients ({admittedPatients.length})</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>MR No</th>
                <th>Patient Name</th>
                <th>Admission</th>
                <th>Total Billed</th>
                <th>Total Paid</th>
                <th>Dues</th>
              </tr>
            </thead>
            <tbody>
              {admittedPatients.map(patient => {
                const billInfo = getPatientBillInfo(patient.id);
                return (
                  <tr key={patient.id}>
                    <td className="font-mono text-sm">{patient.mrNo}</td>
                    <td className="font-medium">{patient.name}</td>
                    <td>{patient.admissionDate}</td>
                    <td>Rs. {billInfo.totalBilled.toLocaleString()}</td>
                    <td className="text-green-600">Rs. {billInfo.totalPaid.toLocaleString()}</td>
                    <td className={billInfo.due > 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>
                      Rs. {billInfo.due.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
              {admittedPatients.length === 0 && (
                <tr><td colSpan="6" className="text-center py-6 text-gray-500">No admitted patients</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
// Birth Certificates
const BirthCertificates = ({ birthRecords }) => {
  const { showLoader, hideLoader } = useLoader();
  const [search, setSearch] = useState('');
  const { apiBabies, loading, error, refetch } = useApiBabies();

  useEffect(() => {
    if (loading) {
      showLoader('Loading birth records...');
    } else {
      hideLoader();
    }
  }, [loading]);

  // Use API babies if available, otherwise fall back to local
  const allRecords = apiBabies.length > 0
    ? apiBabies.map(b => ({
        id: b.id,
        babyName: b.babyName && b.babyName !== 'string' ? b.babyName : null,
        sex: b.gender || 'N/A',
        fatherName: b.fatherName && b.fatherName !== 'string' ? b.fatherName : 'N/A',
        motherName: b.motherName && b.motherName !== 'string' ? b.motherName : 'N/A',
        dateOfBirth: b.dateOfBirth ? new Date(b.dateOfBirth).toLocaleDateString() : 'N/A',
        timeOfBirth: b.birthTime ? new Date(b.birthTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : 'N/A',
        amPm: b.amPm || '',
        birthWeight: b.weightKg || 'N/A',
        modeOfDelivery: b.modeOfDelivery && b.modeOfDelivery !== 'string' ? b.modeOfDelivery : 'N/A',
        address: b.address && b.address !== 'string' ? b.address : 'N/A',
        cellNo: b.cellNo && b.cellNo !== 'string' ? b.cellNo : 'N/A',
        apgarScore: b.apgarScore ?? 'N/A',
        certificateNo: `BC-API-${b.id}`,
        _fromApi: true,
        babyId: b.id,
printCount: b.count ?? 0,      }))
    : birthRecords;

  const filteredRecords = allRecords.filter(b =>
    b.babyName?.toLowerCase().includes(search.toLowerCase()) ||
    b.certificateNo?.toLowerCase().includes(search.toLowerCase()) ||
    b.fatherName?.toLowerCase().includes(search.toLowerCase()) ||
    b.motherName?.toLowerCase().includes(search.toLowerCase())
  );

 const handlePrint = (record) => {
  // MUST open window synchronously before any async — popup blockers kill it otherwise
  const printWindow = window.open('', '_blank');

  // Background API call — does NOT block print
  if (record.babyId) {
    fetch('https://api.ruqqiasultanaclinic.com/api/Patient/babyrecordupdate', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'X-API-TOKEN': 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: record.babyId, count: 1 }),
    })
      .then(() => refetch())
      .catch(err => console.warn('Print count update failed:', err.message));
  }


    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Birth Certificate - ${record.certificateNo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Times New Roman', serif; padding: 40px; background: white; }
          .certificate { max-width: 700px; margin: 0 auto; border: 4px solid #1e40af; padding: 40px; }
          .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { width: 80px; height: 80px; margin: 0 auto 10px; }
          .clinic-name { font-size: 24px; font-weight: bold; color: #1e40af; font-style: italic; margin-bottom: 5px; }
          .address { font-size: 12px; color: #666; }
          .title { font-size: 22px; font-weight: bold; color: #1e40af; text-align: center; margin: 30px 0; }
          .cert-no { text-align: center; margin-bottom: 30px; font-size: 14px; }
          .details { margin-bottom: 30px; }
          .details p { margin: 12px 0; font-size: 14px; }
          .details strong { display: inline-block; width: 150px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-box { text-align: center; }
          .sig-line { border-top: 1px solid #000; width: 150px; margin-bottom: 5px; }
          .sig-label { font-size: 12px; }
          @media print { body { padding: 20px; } .certificate { border-width: 3px; } }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="header">
            <img src="/logo.svg" alt="Logo" class="logo" onerror="this.style.display='none'"/>
            <div class="clinic-name">${DOCTOR_INFO.clinicName}</div>
            <div class="address">${DOCTOR_INFO.address}</div>
            <div class="address">Phone: ${DOCTOR_INFO.phone}</div>
          </div>
          
          <div class="title">BIRTH CERTIFICATE</div>
          <div class="cert-no">Certificate No: <strong>${record.certificateNo}</strong></div>
          
          <div class="details">
            <p><strong>Name:</strong> ${record.babyName || 'Baby of ' + record.motherName}</p>
            <p><strong>Sex:</strong> ${record.sex || 'N/A'}</p>
            <p><strong>Father's Name:</strong> ${record.fatherName || 'N/A'}</p>
            <p><strong>Mother's Name:</strong> ${record.motherName || 'N/A'}</p>
            <p><strong>Date of Birth:</strong> ${record.dateOfBirth || 'N/A'}</p>
            <p><strong>Time of Birth:</strong> ${record.timeOfBirth || 'N/A'} ${record.amPm || ''}</p>
            <p><strong>Birth Weight:</strong> ${record.birthWeight ? record.birthWeight + ' kg' : 'N/A'}</p>
            <p><strong>Mode of Delivery:</strong> ${record.modeOfDelivery || 'N/A'}</p>
            ${record.apgarScore && record.apgarScore !== 'N/A' ? `<p><strong>APGAR Score:</strong> ${record.apgarScore}</p>` : ''}
            <p><strong>Phone:</strong> ${record.cellNo || 'N/A'}</p>
            <p><strong>Address:</strong> ${record.address || 'N/A'}</p>
          </div>
          
          <div class="signatures">
            <div class="sig-box">
              <div class="sig-line"></div>
              <div class="sig-label">Date</div>
            </div>
            <div class="sig-box">
              <div class="sig-line"></div>
              <div class="sig-label">Authorized Signature</div>
            </div>
          </div>
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Birth Certificates</h2>
        <div className="flex items-center gap-3">
          {loading && <span className="text-sm text-blue-500 animate-pulse">🔄 Loading from API...</span>}
          {error && <span className="text-sm text-red-500">⚠️ API Error (showing local data)</span>}
          {!loading && !error && apiBabies.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-600">✅ Live API Data ({apiBabies.length} records)</span>
              <button onClick={refetch} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄 Refresh</button>
            </div>
          )}
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="input-field max-w-xs" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredRecords.map(record => (
          <div key={record.id} className="card p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">👶</span>
              <div className="flex-1">
                <p className="font-bold text-gray-900">{record.babyName || `Baby of ${record.motherName}`}</p>
                <p className="text-xs text-blue-600">{record.certificateNo}</p>
              </div>
              {record.printCount > 0 && (
                <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" title={`Printed ${record.printCount} time(s)`}>
                  {record.printCount}
                </div>
              )}
            </div>
            <div className="text-sm space-y-1 text-gray-600">
              <p><span className="text-gray-400">Father:</span> {record.fatherName}</p>
              <p><span className="text-gray-400">Mother:</span> {record.motherName}</p>
              <p><span className="text-gray-400">DOB:</span> {record.dateOfBirth} at {record.timeOfBirth} {record.amPm}</p>
              <p><span className="text-gray-400">Sex:</span> {record.sex} | <span className="text-gray-400">Weight:</span> {record.birthWeight} kg</p>
              {record.modeOfDelivery && record.modeOfDelivery !== 'N/A' && (
                <p><span className="text-gray-400">Mode:</span> {record.modeOfDelivery}</p>
              )}
            </div>
            <button onClick={() => handlePrint(record)} className="mt-3 w-full btn-primary text-sm py-2">🖨️ Print Certificate</button>
          </div>
        ))}
      </div>

      {filteredRecords.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">No birth records found.</div>
      )}
    </div>
  );
};
// Password Reset
const PasswordReset = ({ users }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
 const [message, setMessage] = useState({ type: '', text: '' });
  const [resetting, setResetting] = useState(false);

  const roles = ['admin', 'doctor', 'receptionist', 'lab'];

  const handleReset = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    if (!selectedRole) { setMessage({ type: 'error', text: 'Select a role' }); return; }
    if (newPassword.length < 6) { setMessage({ type: 'error', text: 'Password must be 6+ characters' }); return; }
    if (newPassword !== confirmPassword) { setMessage({ type: 'error', text: 'Passwords do not match' }); return; }
    setResetting(true);
    try {
      const response = await fetch('https://api.ruqqiasultanaclinic.com/api/Patient/updatepassword/0', {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'X-API-TOKEN': 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: 0, role: selectedRole, salt: selectedRole, password: newPassword }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Password for "${selectedRole}" reset successfully!` });
        setNewPassword(''); setConfirmPassword(''); setSelectedRole('');
      } else {
        const err = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.message || 'Failed to reset password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error: ' + err.message });
    } finally {
      setResetting(false);
    }
  };
  return (
    <div className="max-w-lg mx-auto">
      <div className="card p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">🔐 Reset User Passwords</h2>
        {message.text && <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{message.text}</div>}
        <form onSubmit={handleReset} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select User</label>
           <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="input-field">
              <option value="">-- Select Role --</option>
              {roles.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="input-field" />
          </div>
<button type="submit" disabled={resetting} className={`btn-primary w-full flex items-center justify-center gap-2 transition-all ${resetting ? 'opacity-70 cursor-not-allowed' : ''}`}>
  {resetting ? (
    <>
      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
      </svg>
      Resetting...
    </>
  ) : 'Reset Password'}
</button>        </form>
      </div>
    </div>
  );
};

// PHC Complaints Management (PRE-3)
const ComplaintsManagement = () => {
  const { complaints, addComplaint, updateComplaint } = useData();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ complainantName: '', complainantPhone: '', complainantCNIC: '', patientName: '', mrNo: '', complaintType: '', description: '', status: 'pending' });

  const complaintTypes = ['Medical Care', 'Staff Behavior', 'Billing Issue', 'Cleanliness', 'Waiting Time', 'Communication', 'Other'];

  const handleSubmit = (e) => {
    e.preventDefault();
    addComplaint({ ...form, date: new Date().toISOString().split('T')[0], time: new Date().toLocaleTimeString() });
    setForm({ complainantName: '', complainantPhone: '', complainantCNIC: '', patientName: '', mrNo: '', complaintType: '', description: '', status: 'pending' });
    setShowForm(false);
  };

  const handleStatusChange = (id, status, resolution) => {
    updateComplaint(id, { status, resolution, resolvedDate: status === 'resolved' ? new Date().toISOString().split('T')[0] : null });
  };

  const printComplaint = (complaint) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Complaint Report</title>
      <style>
        body { font-family: Arial; padding: 20px; font-size: 12px; }
        .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 10px; margin-bottom: 15px; }
        .title { font-size: 16px; font-weight: bold; color: #1e40af; }
        .row { display: flex; margin-bottom: 8px; }
        .label { width: 150px; font-weight: bold; }
        .section { margin-bottom: 15px; padding: 10px; background: #f5f5f5; border-radius: 5px; }
      </style></head><body>
        <div class="header"><div class="title">${DOCTOR_INFO.clinicName}</div><div>Complaint Report (PHC Compliance)</div></div>
        <div class="section">
          <div class="row"><span class="label">Complaint ID:</span><span>${complaint.id}</span></div>
          <div class="row"><span class="label">Date:</span><span>${complaint.date} ${complaint.time}</span></div>
          <div class="row"><span class="label">Status:</span><span>${complaint.status.toUpperCase()}</span></div>
        </div>
        <div class="section">
          <div class="row"><span class="label">Complainant:</span><span>${complaint.complainantName}</span></div>
          <div class="row"><span class="label">CNIC:</span><span>${complaint.complainantCNIC || '-'}</span></div>
          <div class="row"><span class="label">Phone:</span><span>${complaint.complainantPhone}</span></div>
          <div class="row"><span class="label">Patient Name:</span><span>${complaint.patientName || '-'}</span></div>
          <div class="row"><span class="label">MR No:</span><span>${complaint.mrNo || '-'}</span></div>
        </div>
        <div class="section">
          <div class="row"><span class="label">Type:</span><span>${complaint.complaintType}</span></div>
          <div class="row"><span class="label">Description:</span></div>
          <p style="margin-top:5px;">${complaint.description}</p>
        </div>
        ${complaint.resolution ? `<div class="section"><div class="row"><span class="label">Resolution:</span></div><p>${complaint.resolution}</p><div class="row"><span class="label">Resolved On:</span><span>${complaint.resolvedDate}</span></div></div>` : ''}
        <div style="margin-top:40px;display:flex;justify-content:space-between;">
          <div style="text-align:center;"><div style="border-top:1px solid #000;width:120px;"></div><div>Complainant</div></div>
          <div style="text-align:center;"><div style="border-top:1px solid #000;width:120px;"></div><div>Admin</div></div>
        </div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">📝 Complaints Management</h2>
          <p className="text-sm text-gray-500">PHC Standard PRE-3: Patient Rights for Appeals and Complaints</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Register Complaint</button>
      </div>

      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">Register New Complaint</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Complainant Name *</label><input type="text" value={form.complainantName} onChange={(e) => setForm({...form, complainantName: e.target.value})} className="input-field" required /></div>
              <div><label className="block text-sm font-medium mb-1">Phone *</label><input type="tel" value={form.complainantPhone} onChange={(e) => setForm({...form, complainantPhone: e.target.value})} className="input-field" required /></div>
              <div><label className="block text-sm font-medium mb-1">CNIC</label><input type="text" value={form.complainantCNIC} onChange={(e) => setForm({...form, complainantCNIC: e.target.value})} className="input-field" /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Patient Name (if applicable)</label><input type="text" value={form.patientName} onChange={(e) => setForm({...form, patientName: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">MR No (if applicable)</label><input type="text" value={form.mrNo} onChange={(e) => setForm({...form, mrNo: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Complaint Type *</label><select value={form.complaintType} onChange={(e) => setForm({...form, complaintType: e.target.value})} className="input-field" required><option value="">Select</option>{complaintTypes.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Description *</label><textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-field" rows="3" required /></div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm">Register Complaint</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>ID</th><th>Date</th><th>Complainant</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {complaints.map(c => (
                <tr key={c.id}>
                  <td className="font-mono text-sm">{c.id}</td>
                  <td>{c.date}</td>
                  <td>{c.complainantName}</td>
                  <td>{c.complaintType}</td>
                  <td><span className={`status-badge ${c.status === 'resolved' ? 'status-discharged' : c.status === 'investigating' ? 'status-partial' : 'status-unpaid'}`}>{c.status}</span></td>
                  <td>
                    <div className="flex gap-1">
                      {c.status !== 'resolved' && (
                        <>
                          <button onClick={() => handleStatusChange(c.id, 'investigating')} className="text-xs px-2 py-1 bg-yellow-100 text-yellow-600 rounded">Investigate</button>
                          <button onClick={() => { const res = prompt('Enter resolution:'); if(res) handleStatusChange(c.id, 'resolved', res); }} className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded">Resolve</button>
                        </>
                      )}
                      <button onClick={() => printComplaint(c)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🖨️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {complaints.length === 0 && <div className="text-center py-8 text-gray-500">No complaints registered</div>}
      </div>
    </div>
  );
};

// PHC Incidents/Sentinel Events Management (CQI-3)
const IncidentsManagement = () => {
  const { incidents, addIncident } = useData();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ incidentType: '', patientName: '', mrNo: '', location: '', description: '', immediateAction: '', reportedBy: '', witnesses: '' });

  const incidentTypes = ['Medication Error', 'Patient Fall', 'Surgical Complication', 'Transfusion Reaction', 'Adverse Drug Reaction', 'Equipment Failure', 'Infection', 'Near Miss', 'Other'];

  const handleSubmit = (e) => {
    e.preventDefault();
    addIncident({ ...form, date: new Date().toISOString().split('T')[0], time: new Date().toLocaleTimeString(), status: 'reported' });
    setForm({ incidentType: '', patientName: '', mrNo: '', location: '', description: '', immediateAction: '', reportedBy: '', witnesses: '' });
    setShowForm(false);
  };

  const printIncident = (incident) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Incident Report</title>
      <style>
        body { font-family: Arial; padding: 20px; font-size: 12px; }
        .header { text-align: center; border-bottom: 2px solid #dc2626; padding-bottom: 10px; margin-bottom: 15px; }
        .title { font-size: 16px; font-weight: bold; color: #dc2626; }
        .row { display: flex; margin-bottom: 8px; }
        .label { width: 150px; font-weight: bold; }
        .section { margin-bottom: 15px; padding: 10px; background: #fef2f2; border-radius: 5px; border: 1px solid #fecaca; }
      </style></head><body>
        <div class="header"><div class="title">${DOCTOR_INFO.clinicName}</div><div>INCIDENT / SENTINEL EVENT REPORT (PHC Compliance)</div></div>
        <div class="section">
          <div class="row"><span class="label">Incident ID:</span><span>${incident.id}</span></div>
          <div class="row"><span class="label">Date/Time:</span><span>${incident.date} ${incident.time}</span></div>
          <div class="row"><span class="label">Type:</span><span style="color:#dc2626;font-weight:bold;">${incident.incidentType}</span></div>
          <div class="row"><span class="label">Location:</span><span>${incident.location}</span></div>
        </div>
        <div class="section">
          <div class="row"><span class="label">Patient Name:</span><span>${incident.patientName || 'N/A'}</span></div>
          <div class="row"><span class="label">MR No:</span><span>${incident.mrNo || 'N/A'}</span></div>
        </div>
        <div class="section">
          <div class="row"><span class="label">Description:</span></div>
          <p style="margin-top:5px;">${incident.description}</p>
        </div>
        <div class="section">
          <div class="row"><span class="label">Immediate Action:</span></div>
          <p style="margin-top:5px;">${incident.immediateAction || 'None documented'}</p>
        </div>
        <div class="section">
          <div class="row"><span class="label">Reported By:</span><span>${incident.reportedBy}</span></div>
          <div class="row"><span class="label">Witnesses:</span><span>${incident.witnesses || 'None'}</span></div>
        </div>
        <div style="margin-top:40px;display:flex;justify-content:space-between;">
          <div style="text-align:center;"><div style="border-top:1px solid #000;width:120px;"></div><div>Reporter</div></div>
          <div style="text-align:center;"><div style="border-top:1px solid #000;width:120px;"></div><div>Head of Department</div></div>
          <div style="text-align:center;"><div style="border-top:1px solid #000;width:120px;"></div><div>Medical Superintendent</div></div>
        </div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">⚠️ Incident / Sentinel Events</h2>
          <p className="text-sm text-gray-500">PHC Standard CQI-3: Sentinel events are intensively analyzed</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Report Incident</button>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 className="font-medium text-red-800 mb-2">Sentinel Events Include:</h4>
        <p className="text-sm text-red-700">Unexpected death, major surgery on wrong patient/site, infant abduction, rape, suicide, transfusion reaction, medication error causing harm, any event causing serious patient harm.</p>
      </div>

      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">Report Incident</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Incident Type *</label><select value={form.incidentType} onChange={(e) => setForm({...form, incidentType: e.target.value})} className="input-field" required><option value="">Select</option>{incidentTypes.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
              <div><label className="block text-sm font-medium mb-1">Location *</label><input type="text" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="input-field" required placeholder="e.g., OT, Ward, Labor Room" /></div>
              <div><label className="block text-sm font-medium mb-1">Reported By *</label><input type="text" value={form.reportedBy} onChange={(e) => setForm({...form, reportedBy: e.target.value})} className="input-field" required /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Patient Name (if applicable)</label><input type="text" value={form.patientName} onChange={(e) => setForm({...form, patientName: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">MR No (if applicable)</label><input type="text" value={form.mrNo} onChange={(e) => setForm({...form, mrNo: e.target.value})} className="input-field" /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Description of Incident *</label><textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-field" rows="3" required /></div>
            <div><label className="block text-sm font-medium mb-1">Immediate Action Taken</label><textarea value={form.immediateAction} onChange={(e) => setForm({...form, immediateAction: e.target.value})} className="input-field" rows="2" /></div>
            <div><label className="block text-sm font-medium mb-1">Witnesses</label><input type="text" value={form.witnesses} onChange={(e) => setForm({...form, witnesses: e.target.value})} className="input-field" placeholder="Names of witnesses" /></div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm">Submit Report</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>ID</th><th>Date</th><th>Type</th><th>Location</th><th>Patient</th><th>Reported By</th><th>Actions</th></tr></thead>
            <tbody>
              {incidents.map(i => (
                <tr key={i.id}>
                  <td className="font-mono text-sm">{i.id}</td>
                  <td>{i.date}<br/><span className="text-xs text-gray-400">{i.time}</span></td>
                  <td><span className="text-red-600 font-medium">{i.incidentType}</span></td>
                  <td>{i.location}</td>
                  <td>{i.patientName || '-'}</td>
                  <td>{i.reportedBy}</td>
                  <td><button onClick={() => printIncident(i)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🖨️ Print</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {incidents.length === 0 && <div className="text-center py-8 text-gray-500">No incidents reported</div>}
      </div>
    </div>
  );
};

// PHC Staff Credentials (HRM-4)
const StaffCredentials = () => {
  const { staffCredentials, addStaffCredential, updateStaffCredential } = useData();
  const { showLoader, hideLoader } = useLoader();
  const { apiStaff, loading: staffLoading, error: staffError, refetch: refetchStaff } = useApiStaff();

  const [showForm, setShowForm]     = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [viewDocs, setViewDocs]     = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: '', designation: '', qualification: '', registrationNo: '',
    registrationBody: '', registrationExpiry: '', cnic: '', phone: '',
    joiningDate: '', specialization: '',
    idCardDoc: '', certificateDoc: '', registrationDoc: '', otherDocs: [],
  });

  useEffect(() => {
    if (staffLoading) showLoader('Loading staff credentials...');
    else hideLoader();
  }, [staffLoading]);

  // ── file → raw base64 (no data:...;base64, prefix) ────────
  const fileToBase64 = (file) =>
    new Promise((res, rej) => {
      const reader   = new FileReader();
      reader.onload  = (e) => res(e.target.result.split(',')[1]);
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });

  // ── base64 → open as PDF in new tab ───────────────────────
  const openBase64PDF = (base64) => {
    const clean  = (base64.includes(',') ? base64.split(',')[1] : base64).replace(/[\s\r\n]/g, '');
    const padded = clean + '='.repeat((4 - (clean.length % 4)) % 4);
    const CHUNK  = 8192;
    const parts  = [];
    for (let i = 0; i < padded.length; i += CHUNK) {
      const slice  = padded.slice(i, i + CHUNK);
      const binary = atob(slice);
      const bytes  = new Uint8Array(binary.length);
      for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
      parts.push(bytes);
    }
    const url = URL.createObjectURL(new Blob(parts, { type: 'application/pdf' }));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // ── map API shape → local shape ───────────────────────────
  const mapApiToLocal = (s) => ({
    id:                 s.id,
    name:               s.fullName           || '',
    cnic:               s.cnic               || '',
    phone:              s.phone              || '',
    designation:        s.designation        || '',
    qualification:      s.qualification      || '',
    specialization:     s.specialization     || '',
    registrationBody:   s.registrationBody   || '',
    registrationNo:     s.registrationNo     || '',
    registrationExpiry: s.registrationExpiry || '',
    joiningDate:        s.joiningDate        || '',
    idCardDoc:          s.cnicDocument       || '',
    certificateDoc:     s.degreeCertificate  || '',
    registrationDoc:    s.registrationCard   || '',
    otherDocs:
      s.otherDocuments && s.otherDocuments !== 'string' && s.otherDocuments !== ''
        ? [{ name: 'Other Document', data: s.otherDocuments }]
        : [],
    _fromApi: true,
  });

  // ── display list: API first, fallback to local ────────────
  const displayStaff = apiStaff.length > 0
    ? apiStaff
        .filter(s => s.fullName && s.fullName !== 'string')
        .map(mapApiToLocal)
    : staffCredentials;

  const designations = [
    'Doctor', 'Nurse', 'Lady Health Visitor (LHV)', 'Midwife', 'Pharmacist',
    'Lab Technician', 'X-Ray Technician', 'Anesthetist', 'OT Technician',
    'Receptionist', 'Other',
  ];
  const registrationBodies = [
    'PMC (Pakistan Medical Commission)', 'PNC (Pakistan Nursing Council)',
    'Pharmacy Council', 'PMDC', 'Other',
  ];

  // ── file input handlers ───────────────────────────────────
  const handleFileChange = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
      alert('Please upload PDF or image files only');
      e.target.value = '';
      return;
    }
    const b64 = await fileToBase64(file);
    setForm(prev => ({ ...prev, [field]: b64 }));
  };

  const handleOtherDocsChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
      alert('Please upload PDF or image files only');
      e.target.value = '';
      return;
    }
    const docName = prompt('Enter document name (e.g., Experience Letter, Training Certificate):');
    if (!docName) return;
    const b64 = await fileToBase64(file);
    setForm(prev => ({ ...prev, otherDocs: [...(prev.otherDocs || []), { name: docName, data: b64 }] }));
  };

  const removeOtherDoc = (index) => {
    setForm(prev => {
      const newDocs = [...(prev.otherDocs || [])];
      newDocs.splice(index, 1);
      return { ...prev, otherDocs: newDocs };
    });
  };

  // ── submit ────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.idCardDoc && !editingId) {
      alert('ID Card document is mandatory!');
      return;
    }

    setSubmitting(true);
    showLoader(editingId ? 'Updating staff...' : 'Creating staff...');

    // API expects otherDocuments as a single base64 string
    const otherDocsBase64 =
      form.otherDocs && form.otherDocs.length > 0 ? form.otherDocs[0].data : '';

    const apiPayload = {
      id:                 editingId ? Number(editingId) : 0,
      fullName:           form.name,
      cnic:               form.cnic,
      phone:              form.phone,
      designation:        form.designation,
      qualification:      form.qualification,
      specialization:     form.specialization,
      registrationBody:   form.registrationBody,
      registrationNo:     form.registrationNo,
      registrationExpiry: form.registrationExpiry,
      joiningDate:        form.joiningDate,
      cnicDocument:       form.idCardDoc       || '',
      degreeCertificate:  form.certificateDoc  || '',
      registrationCard:   form.registrationDoc || '',
      otherDocuments:     otherDocsBase64,
    };

    try {
      if (editingId) {
        await updateStaff(apiPayload);
        updateStaffCredential(editingId, form);
      } else {
        await createStaff(apiPayload);
        addStaffCredential(form);
      }
      await refetchStaff();
      alert(editingId ? '✅ Staff updated!' : '✅ Staff added!');
      resetForm();
    } catch (err) {
      alert(`API failed: ${err.message}. Saved locally.`);
      if (editingId) updateStaffCredential(editingId, form);
      else addStaffCredential(form);
      resetForm();
    } finally {
      setSubmitting(false);
      hideLoader();
    }
  };

  const resetForm = () => {
    setForm({
      name: '', designation: '', qualification: '', registrationNo: '',
      registrationBody: '', registrationExpiry: '', cnic: '', phone: '',
      joiningDate: '', specialization: '',
      idCardDoc: '', certificateDoc: '', registrationDoc: '', otherDocs: [],
    });
    setEditingId(null);
    setShowForm(false);
  };

  // ── edit: fetch fresh data from API to get full base64 ────
  const handleEdit = async (staff) => {
    if (staff._fromApi && staff.id) {
      showLoader('Loading staff details...');
      try {
        const fresh = await fetchStaffById(staff.id);
        setForm(mapApiToLocal(fresh));
      } catch {
        setForm(staff);
      } finally {
        hideLoader();
      }
    } else {
      setForm(staff);
    }
    setEditingId(staff.id);
    setShowForm(true);
  };

  // ── view docs: fetch fresh to get full base64 ─────────────
  const handleViewDocs = async (staff) => {
    if (staff._fromApi && staff.id) {
      showLoader('Loading documents...');
      try {
        const fresh = await fetchStaffById(staff.id);
        setViewDocs(mapApiToLocal(fresh));
      } catch {
        setViewDocs(staff);
      } finally {
        hideLoader();
      }
    } else {
      setViewDocs(staff);
    }
  };

  const printCredential = (staff) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Staff Credential</title>
      <style>
        body { font-family: Arial; padding: 20px; font-size: 12px; }
        .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 10px; margin-bottom: 15px; }
        .title { font-size: 16px; font-weight: bold; color: #1e40af; }
        .row { display: flex; margin-bottom: 8px; }
        .label { width: 180px; font-weight: bold; }
        .section { margin-bottom: 15px; padding: 10px; background: #f0f9ff; border-radius: 5px; border: 1px solid #bae6fd; }
      </style></head><body>
        <div class="header">
          <div class="title">${DOCTOR_INFO.clinicName}</div>
          <div>STAFF CREDENTIAL RECORD (PHC Compliance - HRM-4)</div>
        </div>
        <div class="section">
          <div class="row"><span class="label">Name:</span><span>${staff.name}</span></div>
          <div class="row"><span class="label">CNIC:</span><span>${staff.cnic}</span></div>
          <div class="row"><span class="label">Phone:</span><span>${staff.phone}</span></div>
          <div class="row"><span class="label">Designation:</span><span>${staff.designation}</span></div>
          <div class="row"><span class="label">Specialization:</span><span>${staff.specialization || '-'}</span></div>
        </div>
        <div class="section">
          <div class="row"><span class="label">Qualification:</span><span>${staff.qualification}</span></div>
          <div class="row"><span class="label">Registration Body:</span><span>${staff.registrationBody}</span></div>
          <div class="row"><span class="label">Registration No:</span><span>${staff.registrationNo}</span></div>
          <div class="row"><span class="label">Registration Expiry:</span><span>${staff.registrationExpiry}</span></div>
        </div>
        <div class="section">
          <div class="row"><span class="label">Joining Date:</span><span>${staff.joiningDate}</span></div>
          <div class="row"><span class="label">Documents on File:</span><span>
            ${staff.idCardDoc ? 'ID Card ✓ ' : ''}
            ${staff.certificateDoc ? 'Certificate ✓ ' : ''}
            ${staff.registrationDoc ? 'Registration ✓ ' : ''}
            ${staff.otherDocs?.length ? `+ ${staff.otherDocs.length} other(s)` : ''}
          </span></div>
        </div>
        <div style="margin-top:40px;text-align:right;">
          <div style="display:inline-block;text-align:center;">
            <div style="border-top:1px solid #000;width:150px;"></div>
            <div>Administrator</div>
          </div>
        </div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const diffDays = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const countDocs = (staff) => {
    let count = 0;
    if (staff.idCardDoc)      count++;
    if (staff.certificateDoc) count++;
    if (staff.registrationDoc) count++;
    count += (staff.otherDocs?.length || 0);
    return count;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">🪪 Staff Credentials</h2>
          <p className="text-sm text-gray-500">PHC Standard HRM-4: Credential verification for medical professionals</p>
        </div>
        <div className="flex items-center gap-2">
          {staffLoading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {staffError   && <span className="text-xs text-red-500">⚠️ API Error (showing local)</span>}
          {!staffLoading && !staffError && apiStaff.length > 0 && (
            <span className="text-xs text-green-600">✅ Live API ({displayStaff.length} staff)</span>
          )}
          <button onClick={refetchStaff} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Add Staff</button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">{editingId ? 'Edit' : 'Add'} Staff Credential</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <input type="text" value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CNIC *</label>
                <input type="text" value={form.cnic}
                  onChange={(e) => setForm({...form, cnic: e.target.value})}
                  className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input type="tel" value={form.phone}
                  onChange={(e) => setForm({...form, phone: e.target.value})}
                  className="input-field" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Designation *</label>
                <select value={form.designation}
                  onChange={(e) => setForm({...form, designation: e.target.value})}
                  className="input-field" required>
                  <option value="">Select</option>
                  {designations.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Qualification *</label>
                <input type="text" value={form.qualification}
                  onChange={(e) => setForm({...form, qualification: e.target.value})}
                  className="input-field" required placeholder="e.g., MBBS, BSN, Diploma" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Specialization</label>
                <input type="text" value={form.specialization}
                  onChange={(e) => setForm({...form, specialization: e.target.value})}
                  className="input-field" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Registration Body</label>
                <select value={form.registrationBody}
                  onChange={(e) => setForm({...form, registrationBody: e.target.value})}
                  className="input-field">
                  <option value="">Select</option>
                  {registrationBodies.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Registration No</label>
                <input type="text" value={form.registrationNo}
                  onChange={(e) => setForm({...form, registrationNo: e.target.value})}
                  className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Registration Expiry</label>
                <input type="date" value={form.registrationExpiry}
                  onChange={(e) => setForm({...form, registrationExpiry: e.target.value})}
                  className="input-field" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Joining Date</label>
              <input type="date" value={form.joiningDate}
                onChange={(e) => setForm({...form, joiningDate: e.target.value})}
                className="input-field" />
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-800 mb-3">📄 Document Uploads (PDF or Image)</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    ID Card (CNIC) *{' '}
                    {form.idCardDoc && <span className="text-green-600 font-normal">✓ Uploaded</span>}
                  </label>
                  <input type="file" accept="application/pdf,image/*"
                    onChange={(e) => handleFileChange(e, 'idCardDoc')}
                    className="input-field text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Degree/Certificate{' '}
                    {form.certificateDoc && <span className="text-green-600 font-normal">✓ Uploaded</span>}
                  </label>
                  <input type="file" accept="application/pdf,image/*"
                    onChange={(e) => handleFileChange(e, 'certificateDoc')}
                    className="input-field text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Registration Card{' '}
                    {form.registrationDoc && <span className="text-green-600 font-normal">✓ Uploaded</span>}
                  </label>
                  <input type="file" accept="application/pdf,image/*"
                    onChange={(e) => handleFileChange(e, 'registrationDoc')}
                    className="input-field text-sm" />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1">
                  Other Documents (Experience Letters, Training Certificates, etc.)
                </label>
                <input type="file" accept="application/pdf,image/*"
                  onChange={handleOtherDocsChange}
                  className="input-field text-sm" />
                {form.otherDocs?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.otherDocs.map((doc, i) => (
                      <span key={i} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs flex items-center gap-1">
                        {doc.name}
                        <button type="button" onClick={() => removeOtherDoc(i)}
                          className="text-red-500 hover:text-red-700">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" disabled={submitting}
                className={`btn-primary text-sm ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {submitting ? '⏳ Saving...' : (editingId ? 'Update Staff' : 'Add Staff')}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* View Documents Modal */}
      {viewDocs && (
        <div className="modal-overlay" onClick={() => setViewDocs(null)}>
          <div className="modal-content p-6 max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Documents: {viewDocs.name}</h3>
              <button onClick={() => setViewDocs(null)} className="text-gray-500 text-xl">×</button>
            </div>
            <div className="space-y-3">
              {viewDocs.idCardDoc && (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                  <span>📄 ID Card (CNIC)</span>
                  <button onClick={() => openBase64PDF(viewDocs.idCardDoc)}
                    className="text-blue-600 hover:underline text-sm">View PDF</button>
                </div>
              )}
              {viewDocs.certificateDoc && (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                  <span>📄 Degree/Certificate</span>
                  <button onClick={() => openBase64PDF(viewDocs.certificateDoc)}
                    className="text-blue-600 hover:underline text-sm">View PDF</button>
                </div>
              )}
              {viewDocs.registrationDoc && (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                  <span>📄 Registration Card</span>
                  <button onClick={() => openBase64PDF(viewDocs.registrationDoc)}
                    className="text-blue-600 hover:underline text-sm">View PDF</button>
                </div>
              )}
              {viewDocs.otherDocs?.map((doc, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                  <span>📄 {doc.name}</span>
                  <button onClick={() => openBase64PDF(doc.data)}
                    className="text-blue-600 hover:underline text-sm">View PDF</button>
                </div>
              ))}
              {countDocs(viewDocs) === 0 && (
                <p className="text-gray-500 text-center py-4">No documents uploaded</p>
              )}
            </div>
            <button onClick={() => setViewDocs(null)} className="btn-secondary w-full mt-4">Close</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Emp ID</th><th>Name</th><th>Designation</th><th>Qualification</th>
                <th>Reg. No</th><th>Reg. Expiry</th><th>Docs</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayStaff.map(s => (
                <tr key={s.id}>
                  <td className="font-mono text-sm text-blue-700 font-bold">EMP-{String(s.id).padStart(2, '0')}</td>
                  <td className="font-medium">{s.name}</td>
                  <td>{s.designation}</td>
                  <td>{s.qualification}</td>
                  <td className="font-mono text-sm">{s.registrationNo || '-'}</td>
                  <td>
                    {s.registrationExpiry ? (
                      <span className={`px-2 py-1 rounded text-xs ${
                        isExpired(s.registrationExpiry)
                          ? 'bg-red-100 text-red-700'
                          : isExpiringSoon(s.registrationExpiry)
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {s.registrationExpiry}
                        {isExpired(s.registrationExpiry)     ? ' (EXPIRED)'       : ''}
                        {isExpiringSoon(s.registrationExpiry) ? ' (Expiring Soon)' : ''}
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs ${
                      countDocs(s) > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {countDocs(s)} doc(s)
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(s)}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">Edit</button>
                      <button onClick={() => handleViewDocs(s)}
                        className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded">📄 Docs</button>
                      <button onClick={() => printCredential(s)}
                        className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded">🖨️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {displayStaff.length === 0 && !staffLoading && (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">No staff credentials added</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
export const printPatientRecord = (patient, bills = [], medicalRecords = [], labReports = [], externalLabs = []) => {

  /* ── billing totals ───────────────────────────────────────── */
  const totalBilled = bills.reduce((s, b) => s + Number(b.totalAmount || 0), 0)
    || patient._apiTotalBill || 0;
  const totalPaid   = bills.reduce((s, b) => s + Number(b.paidAmount  || 0), 0)
    || patient._apiPaid || 0;
  const totalDue    = bills.length
    ? bills.reduce((s, b) => s + Number(b.remainingAmount || 0), 0)
    : (patient._apiDue || 0);

  /* ── baby helper ──────────────────────────────────────────── */
  const baby = patient._babyRecord;
  const hasBaby =
    (baby && baby.babyName) ||
    (patient._babyName && patient._babyName !== 'string');

  const babyName   = baby?.babyName       || patient._babyName   || `Baby of ${patient.name}`;
  const babySex    = baby?.gender         || patient._babySex    || 'N/A';
  const babyDOB    = baby?.dateOfBirth
    ? new Date(baby.dateOfBirth).toLocaleDateString()
    : patient._babyDOB || 'N/A';
  const babyTime   = baby?.birthTime
    ? new Date(baby.birthTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  const babyAmPm   = baby?.amPm || '';
  const babyWeight = baby?.weightKg       || patient._babyWeight || 'N/A';
  const babyMode   = baby?.modeOfDelivery || patient._babyMode   || 'N/A';

  /* ── bill rows ────────────────────────────────────────────── */
  const billRows = bills.length
    ? bills.map((bill, idx) => {
        const statusColor = bill.status === 'paid'
          ? '#16a34a' : bill.status === 'partial' ? '#d97706' : '#dc2626';
        const items = [
          ['Operation (C/Sec)',          bill.operation_CSec],
          ['Operation (TAH)',            bill.operation_TAH],
          ['Operation (Vag Hyst)',       bill.operation_VagHyst],
          ['Operation (Myomectomy)',     bill.operation_Myomectomy],
          ['Op. (AP Repair/Laparotomy)',bill.operation_APRepair_Laparotomy],
          ['Operation (NVD/IUD)',        bill.operation_NVD_IUD],
          ['Operation Fee',              bill.operationFee],
          ['Anesthesia Fee',             bill.anesthesiaFee],
          ['OT / Labor Room',            bill.oT_LaborRoomCharges],
          ['NVD / IUD Charges',          bill.nvD_IUD_Charges],
          ['D & C Charges',              bill.dC_Charges],
          ['Room Rent',                  bill.roomRent],
          ['Child Specialist Fee',       bill.childSpecialistFee],
          ['Medicine Bill',              bill.medicineBill],
          ['Admission in N.I.C.U',       bill.admission_NICU],
          ['Daycare in N.I.C.U',         bill.daycare_NICU],
          ['Lab Test Charges',           bill.labTestCharges],
        ].filter(([, v]) => v && Number(v) > 0);

        const itemsHTML = items.map(([label, amt], i) => `
          <tr>
            <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#6b7280;font-size:11px;">${i + 1}</td>
            <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${label}</td>
            <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;text-align:right;font-size:12px;">
              Rs. ${Number(amt).toLocaleString()}
            </td>
          </tr>`).join('');

        return `
          <div style="margin-bottom:16px;">
            ${bills.length > 1
              ? `<p style="font-size:11px;font-weight:700;color:#6b7280;margin-bottom:6px;text-transform:uppercase;letter-spacing:.05em;">
                   Bill #${idx + 1} &nbsp;·&nbsp;
                   ${bill.billDate ? new Date(bill.billDate).toLocaleDateString() : ''}
                 </p>`
              : ''}
            ${items.length ? `
            <table style="width:100%;border-collapse:collapse;margin-bottom:8px;background:#fff;border-radius:6px;overflow:hidden;border:1px solid #e5e7eb;">
              <thead>
                <tr style="background:#eff6ff;">
                  <th style="padding:7px 10px;text-align:left;font-size:11px;color:#1e40af;width:32px;">#</th>
                  <th style="padding:7px 10px;text-align:left;font-size:11px;color:#1e40af;">Particulars</th>
                  <th style="padding:7px 10px;text-align:right;font-size:11px;color:#1e40af;">Amount (Rs.)</th>
                </tr>
              </thead>
              <tbody>${itemsHTML}</tbody>
            </table>` : ''}
            <table style="width:100%;border-collapse:collapse;">
              <tr>
                <td style="padding:6px 10px;font-size:12px;color:#374151;">Total Billed</td>
                <td style="padding:6px 10px;text-align:right;font-size:12px;font-weight:700;">
                  Rs. ${Number(bill.totalAmount || 0).toLocaleString()}
                </td>
              </tr>
              ${Number(bill.discount||0) > 0 ? `
              <tr>
                <td style="padding:6px 10px;font-size:12px;color:#374151;">Discount</td>
                <td style="padding:6px 10px;text-align:right;font-size:12px;color:#d97706;font-weight:600;">
                  - Rs. ${Number(bill.discount).toLocaleString()}
                </td>
              </tr>` : ''}
              <tr>
                <td style="padding:6px 10px;font-size:12px;color:#374151;">Amount Paid</td>
                <td style="padding:6px 10px;text-align:right;font-size:12px;color:#16a34a;font-weight:700;">
                  Rs. ${Number(bill.paidAmount || 0).toLocaleString()}
                </td>
              </tr>
              <tr style="border-top:2px solid #1e40af;">
                <td style="padding:8px 10px;font-size:13px;font-weight:700;">Balance Due</td>
                <td style="padding:8px 10px;text-align:right;font-size:14px;font-weight:800;color:${statusColor};">
                  Rs. ${Number(bill.remainingAmount || 0).toLocaleString()}
                </td>
              </tr>
            </table>
          </div>`;
      }).join('<hr style="border:none;border-top:1px dashed #d1d5db;margin:12px 0;">')
    : `
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="padding:8px 10px;font-size:12px;">Total Billed</td>
          <td style="padding:8px 10px;text-align:right;font-weight:700;">
            Rs. ${totalBilled.toLocaleString()}
          </td>
        </tr>
        <tr>
          <td style="padding:8px 10px;font-size:12px;color:#16a34a;">Amount Paid</td>
          <td style="padding:8px 10px;text-align:right;font-weight:700;color:#16a34a;">
            Rs. ${totalPaid.toLocaleString()}
          </td>
        </tr>
        <tr style="border-top:2px solid #1e40af;">
          <td style="padding:8px 10px;font-size:13px;font-weight:700;">Balance Due</td>
          <td style="padding:8px 10px;text-align:right;font-size:14px;font-weight:800;color:${totalDue > 0 ? '#dc2626' : '#16a34a'};">
            Rs. ${totalDue.toLocaleString()}
          </td>
        </tr>
      </table>`;

  /* ── medical records rows ─────────────────────────────────── */
  const medRows = medicalRecords.length
    ? medicalRecords.map(r => `
        <tr>
          <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#6b7280;">
            ${r.createdDate ? new Date(r.createdDate).toLocaleDateString() : r.date || '-'}
          </td>
          <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${r.recordType || r.type || '-'}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${r.medication || '-'}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${r.descriptionNotes || r.description || '-'}</td>
        </tr>`).join('')
   : `<tr><td colspan="4" style="padding:10px;text-align:center;color:#9ca3af;font-size:12px;">No medical records found</td></tr>`;

  /* ── external lab rows ───────────────────────────────────── */
  const externalLabHTML = externalLabs.length > 0 ? `
    <div style="margin-bottom:16px;">
      <div class="sec-title">🧪 External Lab Reports</div>
      ${externalLabs.map(ext => `
        <table class="section-table" style="margin-bottom:10px;">
          <tr>
            <td colspan="2" style="padding:6px 10px;font-weight:700;background:#eff6ff;color:#1e40af;">
              Date: ${ext.testDate || '-'} ${ext.ageSex ? '&nbsp;|&nbsp; Age/Sex: ' + ext.ageSex : ''} ${ext.patientName ? '&nbsp;|&nbsp; ' + ext.patientName : ''}
            </td>
          </tr>
          ${[
            ['HBsAg', ext.hBsAg], ['HCV', ext.hcv], ['Blood Sugar (Random)', ext.bloodSugarRandom],
            ['Blood Group', ext.bloodGroup], ['Rh Factor', ext.rhFactor], ['ALT (SGPT)', ext.alT_SGPT],
            ['ALP', ext.alp], ['Bilirubin (Total)', ext.bilirubinTotal], ['Urea', ext.urea],
            ['Creatinine', ext.creatinine], ['Uric Acid', ext.uricAcid], ['PT (Prothrombin Time)', ext.pT_ProthrombinTime],
            ['APTT', ext.aptt], ['Hemoglobin', ext.hemoglobin], ['Total WBC Count', ext.totalWBCCount],
            ['Platelet Count', ext.plateletCount],
          ].filter(([, v]) => v).map(([label, val]) => `
            <tr>
              <td style="padding:5px 10px;width:180px;color:#6b7280;">${label}</td>
              <td style="padding:5px 10px;font-weight:600;">${val}</td>
            </tr>
          `).join('')}
        </table>
      `).join('')}
    </div>
  ` : '';

  /* ── HTML ─────────────────────────────────────────────────── */
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Patient Record - ${patient.mrNo}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Segoe UI',Arial,sans-serif;background:#fff;color:#111827;font-size:13px;padding:28px 32px;}
    .page{max-width:860px;margin:0 auto;}

    /* ── header ── */
    .hdr{text-align:center;padding-bottom:14px;margin-bottom:18px;border-bottom:3px double #1e40af;}
    .hdr-logo{width:62px;height:62px;margin:0 auto 8px;}
    .hdr-name{font-size:20px;font-weight:800;color:#1e40af;letter-spacing:.01em;}
    .hdr-addr{font-size:11px;color:#6b7280;margin-top:3px;}
    .hdr-divider{border:none;border-top:1px solid #bfdbfe;margin:8px 0;}

    /* ── section title ── */
    .sec-title{font-size:12px;font-weight:700;color:#1e40af;text-transform:uppercase;
      letter-spacing:.08em;margin-bottom:8px;padding-bottom:4px;border-bottom:1.5px solid #bfdbfe;}

    /* ── doc title ── */
    .doc-title{text-align:center;font-size:16px;font-weight:800;letter-spacing:.08em;
      color:#111827;text-transform:uppercase;margin:4px 0 18px;padding:8px 0;
      border-top:1.5px solid #1e40af;border-bottom:1.5px solid #1e40af;}

    /* ── info grid ── */
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 32px;margin-bottom:16px;}
    .info-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px 14px;}
    .info-row{display:flex;padding:4px 0;border-bottom:1px solid #f3f4f6;font-size:12px;}
    .info-row:last-child{border-bottom:none;}
    .info-label{color:#6b7280;min-width:110px;flex-shrink:0;}
    .info-val{color:#111827;font-weight:600;}

    /* ── PHC box ── */
    .phc-box{background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;
      padding:12px 14px;margin-bottom:16px;}
    .phc-grid{display:grid;grid-template-columns:1fr 1fr;gap:4px 32px;}
    .phc-row{display:flex;font-size:12px;padding:3px 0;}
    .phc-label{color:#6b7280;min-width:130px;flex-shrink:0;}
    .phc-val{color:#1e40af;font-weight:600;}

    /* ── baby box ── */
    .baby-box{background:#fff7ed;border:1px solid #fed7aa;border-radius:6px;
      padding:12px 14px;margin-bottom:16px;}
    .baby-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px 24px;}
    .baby-row{display:flex;font-size:12px;padding:3px 0;}
    .baby-label{color:#9a3412;min-width:90px;flex-shrink:0;}
    .baby-val{color:#111827;font-weight:600;}

    /* ── table shared ── */
    .section-table{width:100%;border-collapse:collapse;margin-bottom:16px;
      border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;}
    .section-table thead tr{background:#eff6ff;}
    .section-table th{padding:7px 10px;text-align:left;font-size:11px;color:#1e40af;
      font-weight:700;text-transform:uppercase;letter-spacing:.04em;}
    .section-table td{padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:12px;}
    .section-table tr:last-child td{border-bottom:none;}

    /* ── billing totals footer ── */
    .bill-footer{background:#f0fdf4;border:1.5px solid #bbf7d0;border-radius:6px;
      padding:12px 14px;margin-bottom:16px;}
    .bill-footer-row{display:flex;justify-content:space-between;padding:5px 0;
      border-bottom:1px solid #d1fae5;font-size:13px;}
    .bill-footer-row:last-child{border-bottom:none;padding-top:8px;margin-top:4px;
      border-top:2px solid #1e40af;}
    .bf-label{color:#374151;}
    .bf-total{font-weight:700;color:#111827;}
    .bf-paid{font-weight:700;color:#16a34a;}
    .bf-due-pos{font-weight:800;font-size:15px;color:#dc2626;}
    .bf-due-zero{font-weight:800;font-size:15px;color:#16a34a;}

    /* ── signatures ── */
    .sig-row{display:flex;justify-content:space-between;margin-top:36px;}
    .sig-box{text-align:center;}
    .sig-line{border-top:1.5px solid #374151;width:150px;margin:0 auto 6px;}
    .sig-label{font-size:11px;color:#6b7280;}

    @media print{
      body{padding:16px 20px;}
      .page{max-width:100%;}
    }
  </style>
</head>
<body>
<div class="page">

  <!-- HEADER -->
  <div class="hdr">
    <img src="/logo.svg" alt="Logo" class="hdr-logo" onerror="this.style.display='none'"/>
    <div class="hdr-name">${DOCTOR_INFO.clinicName}</div>
    <div class="hdr-addr">${DOCTOR_INFO.address}</div>
    <div class="hdr-addr" style="margin-top:2px;">Ph: ${DOCTOR_INFO.phone}</div>
  </div>

  <div class="doc-title">Complete Patient Record</div>

  <!-- PATIENT INFO + ADMISSION DETAILS -->
  <div class="info-grid">
    <div class="info-box">
      <div class="sec-title">Patient Information</div>
      <div class="info-row"><span class="info-label">MR Number:</span><span class="info-val">${patient.mrNo || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Name:</span><span class="info-val">${patient.name || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">CNIC:</span><span class="info-val">${patient.cnic || '-'}</span></div>
      <div class="info-row"><span class="info-label">Husband:</span><span class="info-val">${patient.husbandName || '-'}</span></div>
      <div class="info-row"><span class="info-label">Father:</span><span class="info-val">${patient.fatherName || '-'}</span></div>
      <div class="info-row"><span class="info-label">Age:</span><span class="info-val">${patient.age || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Blood Group:</span><span class="info-val">${patient.bloodGroup || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Phone:</span><span class="info-val">${patient.phone || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Address:</span><span class="info-val">${patient.address || 'N/A'}</span></div>
    </div>
    <div class="info-box">
      <div class="sec-title">Admission Details</div>
      <div class="info-row"><span class="info-label">Room:</span><span class="info-val">${patient.room || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Admission:</span><span class="info-val">${patient.admissionDate || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Discharge:</span><span class="info-val">${patient.dischargeDate || 'Still Admitted'}</span></div>
      <div class="info-row"><span class="info-label">Diagnosis:</span><span class="info-val">${patient.diseases || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Procedure:</span><span class="info-val">${patient.procedure || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Doctor:</span><span class="info-val">${patient.doctorName || 'N/A'}</span></div>
      <div class="info-row"><span class="info-label">Status:</span>
        <span class="info-val" style="color:${patient.status === 'admitted' ? '#16a34a' : '#6b7280'};">
          ${(patient.status || 'N/A').charAt(0).toUpperCase() + (patient.status || '').slice(1)}
        </span>
      </div>
    </div>
  </div>

  <!-- MEDICAL RECORDS -->
  <div style="margin-bottom:16px;">
    <div class="sec-title">Medical Records</div>
    <table class="section-table">
      <thead>
        <tr>
          <th style="width:110px;">Date</th>
          <th>Type</th>
          <th>Medication</th>
          <th>Notes / Description</th>
        </tr>
      </thead>
      <tbody>${medRows}</tbody>
    </table>
  </div>

  ${hasBaby ? `
  <!-- BABY DETAILS -->
  <div class="baby-box">
    <div class="sec-title" style="color:#9a3412;border-color:#fed7aa;">👶 Baby Details (Delivery)</div>
    <div class="baby-grid">
      <div class="baby-row"><span class="baby-label">Baby Name:</span><span class="baby-val">${babyName}</span></div>
      <div class="baby-row"><span class="baby-label">Sex:</span><span class="baby-val">${babySex}</span></div>
      <div class="baby-row"><span class="baby-label">Date of Birth:</span><span class="baby-val">${babyDOB}</span></div>
      ${babyTime ? `<div class="baby-row"><span class="baby-label">Time:</span><span class="baby-val">${babyTime} ${babyAmPm}</span></div>` : ''}
      <div class="baby-row"><span class="baby-label">Birth Weight:</span><span class="baby-val">${babyWeight} kg</span></div>
      <div class="baby-row"><span class="baby-label">Mode:</span><span class="baby-val">${babyMode}</span></div>
      ${baby?.motherName ? `<div class="baby-row"><span class="baby-label">Mother:</span><span class="baby-val">${baby.motherName}</span></div>` : ''}
      ${baby?.fatherName ? `<div class="baby-row"><span class="baby-label">Father:</span><span class="baby-val">${baby.fatherName}</span></div>` : ''}
      ${baby?.apgarScore != null ? `<div class="baby-row"><span class="baby-label">APGAR Score:</span><span class="baby-val">${baby.apgarScore}</span></div>` : ''}
      ${baby?.cellNo ? `<div class="baby-row"><span class="baby-label">Phone:</span><span class="baby-val">${baby.cellNo}</span></div>` : ''}
    </div>
  </div>` : ''}

  <!-- BILLING HISTORY -->
  <div style="margin-bottom:16px;">
    <div class="sec-title">Billing History</div>
    ${bills.length ? `
    <table class="section-table" style="margin-bottom:12px;">
      <thead>
        <tr>
          <th>Date</th>
          <th>Items</th>
          <th style="text-align:right;">Total</th>
          <th style="text-align:right;">Paid</th>
          <th style="text-align:right;">Status</th>
        </tr>
      </thead>
      <tbody>
        ${bills.map(b => {
          const itemNames = [
            b.operation_CSec            && 'Operation (C/Sec)',
            b.operation_TAH             && 'Operation (TAH)',
            b.operation_VagHyst         && 'Operation (Vag Hyst)',
            b.operation_Myomectomy      && 'Myomectomy',
            b.operationFee              && 'Operation Fee',
            b.anesthesiaFee             && 'Anesthesia Fee',
            b.oT_LaborRoomCharges       && 'OT/Labor Room',
            b.roomRent                  && 'Room Rent',
            b.medicineBill              && 'Medicine Bill',
            b.labTestCharges            && 'Lab Test Charges',
            b.childSpecialistFee        && 'Child Specialist',
            b.admission_NICU            && 'Admission in N.I.C.U',
            b.daycare_NICU              && 'Daycare in N.I.C.U',
            b.nvD_IUD_Charges           && 'NVD / IUD Charges',
            b.dC_Charges                && 'D & C Charges',
          ].filter(Boolean);
          const statusColor = b.status === 'paid' ? '#16a34a'
            : b.status === 'partial' ? '#d97706' : '#dc2626';
          return `<tr>
            <td>${b.billDate ? new Date(b.billDate).toLocaleDateString() : '-'}</td>
            <td style="color:#374151;">${itemNames.join(', ') || 'Bill'}</td>
            <td style="text-align:right;font-weight:600;">Rs. ${Number(b.totalAmount||0).toLocaleString()}</td>
            <td style="text-align:right;color:#16a34a;font-weight:600;">Rs. ${Number(b.paidAmount||0).toLocaleString()}</td>
            <td style="text-align:right;">
              <span style="background:${b.status==='paid'?'#dcfce7':b.status==='partial'?'#fef9c3':'#fee2e2'};
                color:${statusColor};padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700;">
                ${(b.status||'unpaid').toUpperCase()}
              </span>
            </td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>` : ''}
    <!-- Billing totals footer -->
    <div class="bill-footer">
      <div class="bill-footer-row">
        <span class="bf-label">Total Billed</span>
        <span class="bf-total">Rs. ${totalBilled.toLocaleString()}</span>
      </div>
      <div class="bill-footer-row">
        <span class="bf-label">Total Paid</span>
        <span class="bf-paid">Rs. ${totalPaid.toLocaleString()}</span>
      </div>
      <div class="bill-footer-row">
        <span class="bf-label" style="font-weight:700;">Balance Due</span>
        <span class="${totalDue > 0 ? 'bf-due-pos' : 'bf-due-zero'}">
          Rs. ${totalDue.toLocaleString()}
        </span>
      </div>
    </div>
  </div>
  ${externalLabHTML}

  <!-- SIGNATURES -->
  <div class="sig-row">
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-label">Patient / Attendant</div>
    </div>
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-label">Admin</div>
    </div>
  </div>

</div><!-- /page -->
<script>window.onload=function(){window.print();}</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
};


// ============================================================
// IMPROVED Patient View Modal — replace the existing
// {viewPatient && (...)} block in PatientRecords component
// ============================================================
/*
  Paste the JSX below in place of the existing modal.
  Make sure printPatientRecord is imported / accessible.
*/

const PatientViewModal_JSX = `
{viewPatient && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
    onClick={() => { setViewPatient(null); setViewLabReports([]); setViewMedicalRecords([]); setViewExternalLabs([]); }}

  >
    <div
      className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {/* ── Modal header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-700 to-blue-500 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-xl">🏥</div>
          <div>
            <h3 className="text-white font-bold text-lg leading-tight">{viewPatient.name}</h3>
            <p className="text-blue-100 text-xs">{viewPatient.mrNo} · {viewPatient.status}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => printPatientRecord(viewPatient, viewPatientBills, viewMedicalRecords, viewLabReports)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-blue-700 rounded-xl text-sm font-semibold hover:bg-blue-50 transition"
          >
            🖨️ Print Record
          </button>
          <button
            onClick={() => { setViewPatient(null); setViewLabReports([]); setViewMedicalRecords([]); setViewExternalLabs([]); }}

            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 text-white hover:bg-white/30 transition text-lg font-bold"
          >×</button>
        </div>
      </div>

      {/* ── Modal body (scrollable) ── */}
      <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

        {loadingDetail && (
          <div className="flex flex-col items-center justify-center py-16 text-blue-500">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3"/>
            <p className="text-sm animate-pulse">Loading patient details…</p>
          </div>
        )}

        {!loadingDetail && (<>

          {/* Patient Info + Admission */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-blue-700 text-xs font-bold uppercase tracking-widest mb-3">Patient Information</p>
              {[
                ['MR Number', viewPatient.mrNo],
                ['Full Name', viewPatient.name],
                ['CNIC', viewPatient.cnic],
                ['Husband', viewPatient.husbandName],
                ['Father', viewPatient.fatherName],
                ['Age', viewPatient.age],
                ['Blood Group', viewPatient.bloodGroup],
                ['Phone', viewPatient.phone],
                ['Address', viewPatient.address],
              ].map(([label, val]) => (
                <div key={label} className="flex text-sm py-1.5 border-b border-blue-100 last:border-0">
                  <span className="text-gray-400 w-28 flex-shrink-0">{label}:</span>
                  <span className="font-semibold text-gray-800">{val || 'N/A'}</span>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">Admission Details</p>
              {[
                ['Room', viewPatient.room],
                ['Admitted', viewPatient.admissionDate],
                ['Discharged', viewPatient.dischargeDate || 'Still Admitted'],
                ['Diagnosis', viewPatient.diseases],
                ['Procedure', viewPatient.procedure],
                ['Doctor', viewPatient.doctorName],
                ['Status', viewPatient.status],
              ].map(([label, val]) => (
                <div key={label} className="flex text-sm py-1.5 border-b border-gray-200 last:border-0">
                  <span className="text-gray-400 w-28 flex-shrink-0">{label}:</span>
                  <span className="font-semibold text-gray-800">{val || 'N/A'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Baby Details */}
          {(() => {
            const baby = viewPatient._babyRecord;
            const hasBabyRecord = baby && baby.babyName;
            const hasBabyFromDetails = viewPatient._babyName && viewPatient._babyName !== 'string';
            if (!hasBabyRecord && !hasBabyFromDetails) return null;
            return (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-orange-700 text-xs font-bold uppercase tracking-widest mb-3">👶 Baby Details</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
                  {(hasBabyRecord ? [
                    ['Name', baby.babyName],
                    ['Sex', baby.gender],
                    ['DOB', baby.dateOfBirth ? new Date(baby.dateOfBirth).toLocaleDateString() : 'N/A'],
                    ['Time', baby.birthTime ? new Date(baby.birthTime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) + ' ' + (baby.amPm||'') : 'N/A'],
                    ['Weight', baby.weightKg ? baby.weightKg + ' kg' : 'N/A'],
                    ['Mode', baby.modeOfDelivery],
                    ['Mother', baby.motherName],
                    ['Father', baby.fatherName],
                    ['APGAR', baby.apgarScore ?? 'N/A'],
                    ['Phone', baby.cellNo],
                    ['Address', baby.address],
                  ] : [
                    ['Name', viewPatient._babyName],
                    ['Sex', viewPatient._babySex],
                    ['DOB', viewPatient._babyDOB],
                    ['Weight', viewPatient._babyWeight ? viewPatient._babyWeight + ' kg' : 'N/A'],
                    ['Mode', viewPatient._babyMode],
                    ['Certificate', viewPatient._babyCertificate],
                  ]).filter(([,v]) => v && v !== 'N/A').map(([label, val]) => (
                    <div key={label} className="flex text-sm py-1">
                      <span className="text-orange-400 w-20 flex-shrink-0">{label}:</span>
                      <span className="font-semibold text-gray-800">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Billing */}
          <div>
            <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">💰 Billing Details</p>
            {viewPatientBills.length > 0 ? viewPatientBills.map((bill, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden mb-3">
                <div className="bg-gray-50 px-4 py-2 flex justify-between items-center border-b">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Bill #{idx + 1}</span>
                  <span className={\`text-xs px-2 py-0.5 rounded-full font-bold \${
                    bill.status === 'paid' ? 'bg-green-100 text-green-700'
                    : bill.status === 'partial' ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-700'}\`}>{(bill.status||'').toUpperCase()}</span>
                </div>
                <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {[
                    ['Operation (C/Sec)', bill.operation_CSec],
                    ['Operation (TAH)', bill.operation_TAH],
                    ['Operation (Vag Hyst)', bill.operation_VagHyst],
                    ['Myomectomy', bill.operation_Myomectomy],
                    ['Operation Fee', bill.operationFee],
                    ['Anesthesia', bill.anesthesiaFee],
                    ['OT / Labor Room', bill.oT_LaborRoomCharges],
                    ['Room Rent', bill.roomRent],
                    ['Medicine', bill.medicineBill],
                    ['Lab Tests', bill.labTestCharges],
                    ['Child Specialist', bill.childSpecialistFee],
                    ['NICU Admission', bill.admission_NICU],
                    ['NICU Daycare', bill.daycare_NICU],
                    ['NVD / IUD', bill.nvD_IUD_Charges],
                    ['D & C Charges', bill.dC_Charges],
                  ].filter(([,v]) => v > 0).map(([label, val]) => (
                    <div key={label} className="flex justify-between bg-gray-50 rounded-lg px-3 py-1.5">
                      <span className="text-gray-500 text-xs">{label}</span>
                      <span className="font-semibold text-gray-800 text-xs">Rs. {Number(val).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 text-center border-t bg-gray-50">
                  {[
                    ['Total', Number(bill.totalAmount), 'text-gray-900'],
                    ['Paid', Number(bill.paidAmount), 'text-green-600'],
                    ['Remaining', Number(bill.remainingAmount), bill.remainingAmount > 0 ? 'text-red-600' : 'text-green-600'],
                  ].map(([label, val, cls]) => (
                    <div key={label} className="py-3 border-r last:border-0">
                      <p className="text-xs text-gray-400">{label}</p>
                      <p className={\`font-bold \${cls}\`}>Rs. {val.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )) : (
              <div className="grid grid-cols-3 text-center bg-green-50 border border-green-200 rounded-xl overflow-hidden">
                {[
                  ['Total Billed', viewPatient._apiTotalBill || 0, 'text-gray-900'],
                  ['Paid', viewPatient._apiPaid || 0, 'text-green-600'],
                  ['Balance Due', viewPatient._apiDue || 0, (viewPatient._apiDue||0) > 0 ? 'text-red-600' : 'text-green-600'],
                ].map(([label, val, cls]) => (
                  <div key={label} className="py-4 border-r last:border-0">
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className={\`font-bold \${cls}\`}>Rs. {Number(val).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Medical Records */}
          {viewMedicalRecords.length > 0 && (
            <div className="bg-white border border-purple-100 rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-4 py-3 border-b border-purple-100 flex items-center gap-2">
                <span className="text-purple-700 font-bold text-sm">📋 Medical Records</span>
                <span className="bg-purple-200 text-purple-700 text-xs px-2 py-0.5 rounded-full">{viewMedicalRecords.length}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {viewMedicalRecords.map((rec, i) => (
                  <div key={i} className="px-4 py-3 hover:bg-purple-50/50 transition-colors">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                      {[
                        ['Type', rec.recordType],
                        ['Medication', rec.medication],
                        ['Dosage', rec.dosage],
                        ['Notes', rec.descriptionNotes],
                        ['Complications', rec.complications],
                        ['Date', rec.createdDate ? new Date(rec.createdDate).toLocaleDateString() : null],
                      ].filter(([,v]) => v).map(([label, value]) => (
                        <div key={label}>
                          <span className="text-gray-400 text-xs">{label}: </span>
                          <span className="font-medium text-gray-800">{value}</span>
                        </div>
                      ))}
                    </div>
                    {(rec.reportFile || rec.fileData || rec.base64) && (
                      <button
                        onClick={() => openBase64Doc(rec.reportFile || rec.fileData || rec.base64)}
                        className="mt-2 flex items-center gap-1.5 text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1.5 rounded-lg transition-all">
                        📄 View Document
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lab Reports */}
          {viewLabReports.length > 0 && (
            <div className="bg-white border border-teal-100 rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 px-4 py-3 border-b border-teal-100 flex items-center gap-2">
                <span className="text-teal-700 font-bold text-sm">🧪 Lab Reports</span>
                <span className="bg-teal-200 text-teal-700 text-xs px-2 py-0.5 rounded-full">{viewLabReports.length}</span>
              </div>
              <div className="p-4 grid gap-3">
                {viewLabReports.map((report, i) => {
                  const fileKey = report.reportFile || report.fileData || report.base64 || report.file || report.document;
                  return (
                    <div key={i} className="flex items-center justify-between bg-teal-50 border border-teal-100 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center text-xl">🧾</div>
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">
                            {report.testName || report.reportName || report.name || \`Lab Report \${i + 1}\`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {report.createdDate ? new Date(report.createdDate).toLocaleDateString() : ''}
                            {report.labName ? \` · \${report.labName}\` : ''}
                          </p>
                        </div>
                      </div>
                      {fileKey ? (
                        <button onClick={() => openBase64Doc(fileKey)}
                          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-xs px-4 py-2 rounded-lg transition-all font-medium shadow-sm">
                          👁️ View Report
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1.5 rounded-lg">No file</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </>)}
      </div>

      {/* ── Modal footer ── */}
      <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
        <button
          onClick={() => printPatientRecord(viewPatient, viewPatientBills, viewMedicalRecords, viewLabReports)}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
        >
          🖨️ Print Record
        </button>
        <button
         onClick={() => { setViewPatient(null); setViewLabReports([]); setViewMedicalRecords([]); setViewExternalLabs([]); }}

          className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-300 transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}`;

export default AdminPanel;